import Gantt from "frappe-gantt";
import createElement from "../../../../helpers/createElement";
import {waitUntil} from "../../../../helpers/waitUntil";
import _, {debounce, throttle} from "lodash";
import moment from "moment";
import {createGrid} from "../../../../helpers/grids/grids";
import {createCellRenderer} from "../../../../helpers/grids/helpers";
import {CustomDateCellEditor} from "./cellEditors/customDateCellEditor";
import {createTaskConstraintsModal} from "./modals/taskConstraintsModal";
import {TaskError} from "./Tasks/TaskError";
import {RecursiveConstraintError} from "./Exceptions/RecursiveConstraint";
import {createWorkableDaysModal} from "./modals/workableDaysModal";
import {createTaskDelaysModal} from "./modals/taskDelaysModal";
import {CustomConstraintsCellEditor} from "./cellEditors/customConstraintsCellEditor";
import {addQuarterViewMode} from "./BaseOverrides/addQuarterViewMode";
import {addSidebarCollapseToggleListener} from "../../../admin-panel/collapsibleSidebar";
import {createDisplayTaskModal} from "./modals/displayTaskModal";
import {scrollToElement} from "../../../../helpers/scrolling/scrolling";
import {TimelinePrintModal} from "./modals/TimelinePrintModal";
import {getInputsByName} from "../../../../helpers/forms";
import {hideLoading, showLoading} from "../../../../helpers/loading";

// noinspection JSUnusedGlobalSymbols

/**
 * @param {Element} domElement The container of the gantt wrapper
 * @param {import('./ganttWrapper').GanttWrapper.Task[]} tasks
 * @param {TimelineSettingsDefs.Settings} settings
 * @param {GanttWrapper.Options} options
 * @return {import('./ganttWrapper').GanttWrapper.GanttWrapper}
 */
function createGantt(
	domElement,
	tasks,
	settings,
	options = {}
) {
	const TASK_PANEL_MIN_WIDTH = 220;
	const INITIAL_PANEL_WIDTH = 320;

	const DEFAULT_TASK_DURATION = 5;

	// noinspection JSUnusedGlobalSymbols
	const defaultOptions = {
		bar_height: 32,
		view_modes: ['Quarter Day', 'Half Day', 'Day', 'Week', 'Month'],
		on_date_change: function (task, start, end) {
			task = findTaskById(task.id);

			const currentStartDate = getCurrentStartDate(task);
			const currentEndDate = getCurrentEndDate(task);


			const newStartDate = moment(start);
			const newEndDate = moment(end);

			const startDateChanged = currentStartDate.isSame(newStartDate, 'day') === false;
			const endDateChanged = currentEndDate.isSame(newEndDate, 'day') === false;


			if (startDateChanged && endDateChanged) {
				// Both date changed, 'dragged' a task, change only start date
				// to preserve real task duration
				setTaskStartDate(task, newStartDate, true);
			} else if (startDateChanged) {
				// Only start date changed, change both dates to change duration
				setTaskStartDate(task, newStartDate, false);
				setTaskEndDate(task, newEndDate, false);

				invalidateTaskDependants(task);
				fullRefresh();
			} else if (endDateChanged) {
				// Only end date changed
				setTaskEndDate(task, newEndDate, true);
			}
		},
		on_progress_change: (task, progress) => {
			setTaskProgress(task, progress);
		},
		custom_popup_html: getCustomTaskPopup,
	};

	/**
	 * @type {GanttWrapper.Options}
	 */
	const finalOptions = {
		...defaultOptions,
		...options,
	}

	// Mark gantt as view only if required
	if (options.viewOnly) {
		// Hide most elements if on view only mode
		domElement.classList.add('view-only');
	}

	// noinspection JSValidateTypes
	/** @type {import('./ganttWrapper').GanttWrapper.GanttWrapper} */
	const gantt = new Gantt(
		domElement.querySelector('#timeline-gantt-svg'),
		tasks,
		finalOptions
	);

	// Init custom properties
	gantt._all_tasks = tasks;
	gantt._taskAutoincrementId = getInitialAutoIncrementValue();
	gantt._dependencyLog = [];
	gantt._draggingBackground = false;
	gantt._changingTaskPanelWidth = false;
	gantt._taskPanelWidth = INITIAL_PANEL_WIDTH;
	gantt._lastTaskPanelWidth = INITIAL_PANEL_WIDTH;
	gantt._taskPanelNativeScrolling = false;
	gantt._taskPanelFakeScrolling = false;
	gantt._ganttChartNativeScrolling = false;
	gantt._ganttChartFakeScrolling = false;
	gantt._lastFocusedGridCell = undefined;
	gantt._cachedValues = {};
	gantt._invalidateDependantsIdHistory = [];
	gantt._calculateConstraintsIdHistory = [];
	gantt._settings = settings;
	gantt._gridSelectedTasks = [];
	gantt._refreshing = false;
	gantt._editorViewType = 'split';
	gantt._scrollingToRow = undefined;
	gantt._lastTaskDragEvent = undefined;
	gantt._wrapperOptions = options;

	// Initialize properties related to the timeline form
	const formProperties = getInputsByName(domElement, ['timeline_name', 'related_budget_name']);

	gantt.__timeline_name = formProperties.timeline_name;
	gantt.__related_budget_name = formProperties.related_budget_name;

	// Add public functions
	gantt._addTask = addTask;
	gantt._removeTask = removeTask;
	gantt._setRange = setRange;
	gantt._getTasks = getTasks;
	gantt._getVisibleTasks = getVisibleTasks;
	gantt._findTaskById = findTaskById;
	gantt._getTaskDisplayIndex = getTaskDisplayIndex;
	gantt._getTaskConstraintDescription = getTaskConstraintsTextDescription;
	gantt._getTaskConstraintTextRepresentation = getTaskConstraintsTextRepresentation;
	gantt._getTaskParents = getTaskParents;
	gantt._getCurrentStartDate = getCurrentStartDate;
	gantt._getCurrentEndDate = getCurrentEndDate;
	gantt._getTaskDelayedDays = getDelayedDays;
	gantt._getTaskActualDuration = getTaskActualDuration;
	gantt._showTaskConstraintsModal = showTaskConstraintsModal;
	gantt._getCurrentTasks = getCurrentTasks;
	gantt._focusTask = focusTask;
	gantt._getTimelineMonths = getTimelineMonths;
	gantt._getTimelineQuarters = getTimelineQuarters;
	gantt._traverseDescendants = traverseDescendants;
	gantt._traverseParents = traverseParents;
	gantt._exportTasks = exportTasks;
	gantt._exportTimeline = exportTimeline;
	gantt._exportUISettings = exportUISettings;
	gantt._setViewType = setViewType;
	gantt._fullRefresh = fullRefresh;

	// Add containers DOM elements
	initContainerElements();

	// Override existing methods
	overrideExistingMethods();

	// Init modals
	initModals();

	// Subscribe to sidebar collapse events
	addSidebarCollapseToggleListener(() => {
		// Refresh gantt after layout has finished updating
		setTimeout(
			() => {
				enforceTaskPanelWidthLimits();
				fullRefresh();
			},
			512
		)
	})

	// Prepare existing tasks
	tasks.forEach(task => {
		// Init errors array if missing
		task.errors = [];

		// Init delays & constraints arrays if missing
		if (!task.delays) {
			task.delays = [];
		}
		if (!task.constrainsts) {
			task.constrainsts = [];
		}

		// Update duration from start/end dates
		task.duration = getDurationFromDates(
			getCurrentStartDate(task),
			getCurrentEndDate(task)
		) - getDelayedDays(task)
	})

	showLoading(gantt._container);

	// Finishing touches
	setTimeout(() => {
		// Wait a bit so the underlying gantt has finished initializing
		scrollInnerContainer();

		// Invalidate all tasks
		gantt._getTasks().forEach(task => task.needs_dates_calculation = true);

		// Recalculate end dates in case non workable days somehow were changed
		recalculateEndDates();

		// First render so grid headers are properly displayed
		fullRefresh();

		if (finalOptions.fromTemplate) {
			initializeFromTemplate();
		}

		if (gantt._wrapperOptions.UISettings) {
			loadStoredUISettings();
		}

		if (gantt._wrapperOptions.viewOnly) {
			// Force to gantt editor view
			gantt._editorViewTypeSelect.value = 'gantt';
			handleEditorViewTypeChange()

			// Force to quarter view
			gantt._viewTypeSelect.value = 'Quarter';
			handleViewTypeChange()
		}

		// Make gantt visible after next estimated render
		setTimeout(() => {
			gantt._container.classList.add('initialized');
			hideLoading(gantt._container);

			// Also remove loading overlay icon, just in case it could cause issues
			const loadingIcon = gantt._container.querySelector('.lds-ring');
			if (loadingIcon) {
				loadingIcon.remove();
			}


			// If view only, give it a last refresh, to make sure interaction
			// brokers are setup properly
			if (gantt._wrapperOptions.viewOnly) {
				fullRefresh();
			}
		}, 512)
	}, 64)

	return gantt;

	// Methods

	/**
	 * @param {GanttWrapper.Task} task
	 * @param {number} [index]
	 */
	function addTask(task, index) {
		// Set id
		task.id = gantt._taskAutoincrementId.toString();

		// Add auto generated name
		if (task.name.length === 0) {
			task.name = `New Task #${gantt._taskAutoincrementId}`;
		}

		gantt._taskAutoincrementId++;

		if (index) {
			gantt._all_tasks.splice(index, 0, task);
		} else {
			gantt._all_tasks.push(task);
		}

		// Set initial calculation status
		task.needs_dates_calculation = false;

		// Set initial hidden status
		task.hidden = false;

		// Set initial collapsed status
		task.collapsed = false;

		// Set errors array
		task.errors = [];

		// Mark it as selected so its highlighted after refresh
		gantt._gridSelectedTasks = [task]

		fullRefresh()
	}

	/**
	 * Called when adding a new task from gantt chart button
	 */
	function addInlineTask() {
		addTask({
			start: gantt._inlineAddTaskButton.dataset.start,
			end: gantt._inlineAddTaskButton.dataset.end,
			duration: DEFAULT_TASK_DURATION,
			name: '',
			dependencies: [],
			constraints: [],
			delays: [],
			progress: 0,
			is_milestone: false,
			printable: true,
		})
	}

	/**
	 * Called when adding a new task from toolbar button
	 * */
	function handleSaveRequest() {
		// Request parent caller to handle a save request
		if (gantt._wrapperOptions.onSaveRequest) {
			gantt._wrapperOptions.onSaveRequest()
		}
	}

	/**
	 * Called when adding a new task from toolbar button
	 * */
	function addTaskFromToolbar() {
		// Use either the initial hidden tasks or the last tasks as a reference date
		const referenceTask = gantt._all_tasks[gantt._all_tasks.length - 1];

		const startDate = getClosestWorkDay(getCurrentStartDate(referenceTask));
		const endDate = getEndDateFromDuration(startDate, DEFAULT_TASK_DURATION);

		const currentlySelectedTasks = getSelectedTasks();
		if (currentlySelectedTasks.length) {
			// Add task below selected task
			const lastSelectedTask = currentlySelectedTasks[currentlySelectedTasks.length - 1];

			addNewTaskBelow(lastSelectedTask);
		} else {
			// Add task at the bottom of the list
			addTask({
				start: startDate.toDate(),
				end: endDate.toDate(),
				duration: DEFAULT_TASK_DURATION,
				name: '',
				dependencies: [],
				constraints: [],
				delays: [],
				progress: 0,
				is_milestone: false,
				printable: true,
			})
		}


		// TODO: Scroll gantt to make tasks visible if not already
	}

	/**
	 * @param {GanttWrapper.Task} existingTask
	 * @param {GanttWrapper.Task} [newTask]
	 */
	function addChildTask(existingTask, newTask) {
		if (!newTask) {
			newTask = {
				name: '',
				start: '', // Set later
				end: '', // Set later
				duration: 1, // Set later
				dependencies: [],
				constraints: [],
				delays: [],
				progress: 0,
				is_milestone: false,
				printable: true,
			};
		}

		// Set start, end date and duration based on existing task
		newTask.start = formatDateForGanttChart(getCurrentStartDate(existingTask));
		if (newTask.end === '') {
			newTask.end = formatDateForGanttChart(getCurrentEndDate(existingTask));
		}

		newTask.duration = existingTask.duration;

		// Link to parent
		newTask.dependencies.push(existingTask.id);

		// Add it after the existing task
		const existingTaskIndex = getTaskIndex(existingTask);

		invalidateTaskDependants(newTask, true);
		addTask(newTask, existingTaskIndex + 1);

		hideTaskPopup();
	}

	/**
	 * Returns a new task with the same start & end dates and default duration
	 * as the given task, it does not add it to the task array
	 *
	 * @param {GanttWrapper.Task} existingTask
	 * @return {GanttWrapper.Task}
	 */
	function getNewTaskFromExisting(existingTask) {
		const newTask = {
			name: '',
			start: '', // Set later
			end: '', // Set later
			duration: 1, // Set later
			dependencies: [],
			constraints: [],
			delays: [],
			progress: 0,
			is_milestone: false,
			printable: true,
		};

		// Set start, end date and duration based on existing task
		newTask.start = formatDateForGanttChart(getCurrentStartDate(existingTask));
		newTask.end = formatDateForGanttChart(getEndDateFromDuration(
			getCurrentStartDate(existingTask),
			DEFAULT_TASK_DURATION
		));

		newTask.duration = DEFAULT_TASK_DURATION;

		return newTask;
	}

	/**
	 * Adds a task above the given task at the same indent level
	 *
	 * @param {GanttWrapper.Task} existingTask
	 */
	function addNewTaskAbove(existingTask) {
		const newTask = getNewTaskFromExisting(existingTask);

		const taskParents = getTaskParents(existingTask);

		if (taskParents.length) {
			// Put new task at the same indent level
			setTaskParent(newTask, taskParents[0]);
		}

		// Get existing task index
		const newTaskIndex = getTaskIndex(existingTask);

		// Hide popup if open
		hideTaskPopup()

		// Add task above
		addTask(newTask, newTaskIndex);
	}

	/**
	 * Adds a task below the given task at the same indent level
	 *
	 * @param {GanttWrapper.Task} existingTask
	 */
	function addNewTaskBelow(existingTask) {
		const newTask = getNewTaskFromExisting(existingTask);

		const taskParents = getTaskParents(existingTask);

		if (taskParents.length) {
			// Put new task at the same indent level
			setTaskParent(newTask, taskParents[0]);
		}

		// Get existing task index
		const newTaskIndex = getTaskIndex(existingTask) + 1;

		// Hide popup if open
		hideTaskPopup()

		// Add task below
		addTask(newTask, newTaskIndex);
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @param {boolean} [withFullRefresh]
	 */
	function removeTask(task, withFullRefresh = true) {
		// First delete all dependencies
		const childTasks = getChildTasks(task);

		childTasks.forEach(childTask => {
			if (childTask.dependencies.length === 1) {
				// Only child, remove it but don't refresh yet
				removeTask(childTask, false);
			} else {
				// Remove itself from dependencies list
				childTask.dependencies = childTask.dependencies.filter(taskId => {
					return taskId !== task.id;
				})
			}
		})

		// Remove task from array
		const taskIndex = getTaskIndex(task);
		gantt._all_tasks.splice(taskIndex, 1);

		if (withFullRefresh) {
			invalidateTaskDependants(task);
			fullRefresh()
		}

		// Hide task popup if open
		hideTaskPopup();
	}

	function setRange(startDate, endDate) {
		gantt._all_tasks[0].start = startDate;
		gantt._all_tasks[0].end = endDate;

		fullRefresh()
	}

	function initContainerElements() {
		gantt._container = domElement;
		gantt._ganttContainer = domElement.querySelector('.gantt-container');
		gantt._viewport = domElement.querySelector('.viewport');
		gantt._viewportWrapper = domElement.querySelector('.viewport-wrapper');
		gantt._svgContainer = gantt._ganttContainer.querySelector('svg');

		// Add listener for base container click
		gantt._container.addEventListener(
			'click',
			containerClickHandler,
			{
				capture: true,
			}
		);

		// Add document listener -- If we ever have more than
		// one gantt instance we will need to revisit this
		document.addEventListener(
			'click',
			documentClickHandler,
			{
				capture: true,
			}
		);

		// Add listeners for mouse down and up for the container, to handle dragging
		gantt._container.addEventListener('pointerdown', handleContainerMouseDown);
		gantt._container.addEventListener('pointerup', handleContainerMouseUp);

		// Add listener for base container mouse movement
		gantt._container.addEventListener(
			'pointermove',
			throttle(
				e => handleMouseMoveOverViewport(e),
				16,
				{
					leading: true
				}
			),
		);

		/** Add empty div blocking first bar/title bar from user interaction */
		gantt._titleBarScreen = createElement('<div class="title-bar-screen"></div>');

		gantt._ganttContainer.appendChild(gantt._titleBarScreen);

		/** Add remove relationship icon */
		gantt._removeRelationshipIcon = createElement(`
			<div class="remove-relationship-icon" style="top: 0; left: 0">
				<i class="mdi mdi-close-thick"></i>
			</div>
		`)

		gantt._ganttContainer.appendChild(gantt._removeRelationshipIcon);

		/** Add custom date anchor points container */
		gantt._dateAnchorPointsContainer = createElement(`
			<div class="date-anchor-points-container" style="top: 0; left: 0; width: 0">
			</div>
		`)

		gantt._ganttContainer.appendChild(gantt._dateAnchorPointsContainer);

		/** Add bottoms tools container */
		gantt._bottomToolsContainer = createElement(`
			<div
				class="border-top bottom-tools-container"
				type="button"
				style="top: 0; left: 0"
			>
			</div>
		`)

		gantt._ganttContainer.appendChild(gantt._bottomToolsContainer);

		/** Add inline add task button*/
		gantt._inlineAddTaskButton = createElement(`
			<button
				class="btn btn-success btn-sm add-task-button w-auto text-no-wrap"
				type="button"
				style="top: 0; left: 0"
				data-hide-if-view-only="true"
			>
				Add Task
			</button>
		`)

		gantt._inlineAddTaskButton.addEventListener('click', () => addInlineTask());

		gantt._bottomToolsContainer.appendChild(gantt._inlineAddTaskButton);

		/** Add vertical anchor guide */
		gantt._verticalAnchorGuide = createElement(`
			<div
				class="vertical-anchor-bar hidden"
				type="button"
				style="top: 0; left: 0"
			></div>
		`)

		gantt._ganttContainer.appendChild(gantt._verticalAnchorGuide);

		/** Add cancel selection button*/
		gantt._cancelSelectionButton = createElement(`
			<button
				class="btn btn-danger btn-sm w-auto text-no-wrap cancel-selection-button"
				type="button"
			>
				Cancel
			</button>
		`)

		gantt._cancelSelectionButton.addEventListener('click', () => endTaskSelectionProcess());

		gantt._viewportWrapper.appendChild(gantt._cancelSelectionButton);

		/**
		 * Add Task Panel Headers Container
		 */

		gantt._taskPanelHeaderContainer = createElement(`
			<div
				class="task-panel-headers-container ag-theme-alpine"
			>
				<div class="headers-container ag-root-wrapper ag-layout-normal ag-ltr">
					<div class="ag-root-wrapper-body ag-layout-normal ag-focus-managed" style="height: 100%">
						<div class="ag-root ag-unselectable ag-layout-normal" style="height: 100%">
							<div class="ag-header ag-focus-managed ag-pivot-off" style="height: 100%">
								<div class="ag-header-viewport" style="height: 100%">
									<div class="duplicated-headers-go-here"></div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		`)

		gantt._viewportWrapper.appendChild(gantt._taskPanelHeaderContainer);

		/**
		 * Add task panel container
		 */

		gantt._taskPanelContainer = createElement(`
			<div
				class="task-panel-container"
				data-hide-if-view-only="true"
			>
				<div class="task-panel-width-anchor"></div>
				<div class="task-panel-entries"></div>
			</div>
		`)

		gantt._taskPanelEntriesContainer = gantt._taskPanelContainer.querySelector('.task-panel-entries');

		gantt._viewport.appendChild(gantt._taskPanelContainer);

		initTaskPanelGrid();

		/**
		 * Add Task Panel Scrollbar Container
		 */

			// Create a debounced function to clear native scrolling flag
		const debouncedClearNativeScrollingFlagFunction = _.debounce(
				() => {
					gantt._taskPanelNativeScrolling = false;
				},
				64,
				{
					leading: false,
					trailing: true,
				}
			)

		// Check if task panel is scrolling natively to sync it with its fake scrollbar
		gantt._taskPanelContainer.querySelector('.ag-center-cols-viewport').addEventListener('scroll', () => {
			if (gantt._taskPanelFakeScrolling === false) {
				gantt._taskPanelNativeScrolling = true;
				debouncedClearNativeScrollingFlagFunction();
			}
		})

		gantt._taskPanelScrollbarContainer = createElement(`
			<div
				class="task-panel-scrollbar-container"
			>
				<div class="padding"></div>
			</div>
		`)

		gantt._viewportWrapper.appendChild(gantt._taskPanelScrollbarContainer);

		// Get scrollbar height
		let scrollbarHeight = gantt._taskPanelScrollbarContainer.offsetHeight - gantt._taskPanelScrollbarContainer.clientHeight;
		if (scrollbarHeight < 6) {
			// We add a min height here for browser with hidden scrollbars
			scrollbarHeight = 6;
		}
		// Update element to match scrollbar height plus some minor padding
		gantt._taskPanelScrollbarContainer.style.height = `${scrollbarHeight + 2}px`

		// Create a debounced function to clear fake scrolling flag
		const debouncedClearFakeScrollingFlagFunction = _.debounce(
			() => {
				gantt._taskPanelFakeScrolling = false;
			},
			64,
			{
				leading: false,
				trailing: true,
			}
		)

		// Setup flag when fake scrollbar is scrolling, used to disable the native scrolling event on
		// the task panel container
		gantt._taskPanelScrollbarContainer.addEventListener('scroll', () => {
			if (gantt._taskPanelNativeScrolling === false) {
				gantt._taskPanelFakeScrolling = true;
				debouncedClearFakeScrollingFlagFunction();
			}
		})

		/**
		 * Add Gantt Chart Scrollbar Container
		 */
			// Create a debounced function to clear native scrolling flag
		const debouncedClearNativeScrollingForGanttChartFlagFunction = _.debounce(
				() => {
					gantt._ganttChartNativeScrolling = false;
				},
				64,
				{
					leading: false,
					trailing: true,
				}
			)

		// Check if task panel is scrolling natively to sync it with its fake scrollbar
		gantt._ganttContainer.addEventListener('scroll', () => {
			if (gantt._ganttChartFakeScrolling === false) {
				gantt._ganttChartNativeScrolling = true;
				debouncedClearNativeScrollingForGanttChartFlagFunction();
			}
		})

		gantt._ganttChartScrollbarContainer = createElement(`
			<div
				class="gantt-chart-scrollbar-container"
			>
				<div class="padding"></div>
			</div>
		`)

		gantt._viewportWrapper.appendChild(gantt._ganttChartScrollbarContainer);

		// Get scrollbar height
		// Update element to match scrollbar height plus some minor padding
		gantt._ganttChartScrollbarContainer.style.height = `${scrollbarHeight + 2}px`

		// Create a debounced function to clear fake scrolling flag
		const debouncedClearFakeScrollingOnGanttChartFlagFunction = _.debounce(
			() => {
				gantt._ganttChartFakeScrolling = false;
			},
			64,
			{
				leading: false,
				trailing: true,
			}
		)

		// Setup flag when fake scrollbar is scrolling, used to disable the native scrolling event on
		// the task panel container
		gantt._ganttChartScrollbarContainer.addEventListener('scroll', () => {
			if (gantt._ganttChartNativeScrolling === false) {
				gantt._ganttChartFakeScrolling = true;
				debouncedClearFakeScrollingOnGanttChartFlagFunction();
			}
		})

		/**
		 * Add Gantt dates duplicated sticky container
		 */
		gantt._ganttDatesDuplicatedStickyHeaderContainer = createElement(`
			<div
				class="sticky-dates-container gantt"
			>
				<svg></svg>
			</div>
		`)

		gantt._viewportWrapper.appendChild(gantt._ganttDatesDuplicatedStickyHeaderContainer);

		/**
		 * Init Toolbar
		 */

		initToolbar();

		/**
		 * Init draggable bottom padding
		 */

		gantt._ganttChartDragPaddingContainer = createElement(`
			<div
				class="gantt-chart-drag-padding-container"
			>
			</div>
		`);


		gantt._viewport.appendChild(gantt._ganttChartDragPaddingContainer);

		/**
		 * Build non workable days highlight templates
		 */

		const nonWorkableTimelineHighlightTemplate = document.createElementNS(
			"http://www.w3.org/2000/svg",
			"rect"
		);
		nonWorkableTimelineHighlightTemplate.setAttribute('class', 'non-workable-day-timeline-highlight');

		gantt._ganttChartNotWorkableDayTimelineHighlightTemplate = nonWorkableTimelineHighlightTemplate;

		const nonWorkableTaskHighlightTemplate = document.createElementNS(
			"http://www.w3.org/2000/svg",
			"rect"
		);
		nonWorkableTaskHighlightTemplate.setAttribute('class', 'non-workable-day-task-highlight');

		// Add title to highlight
		const svgTitleElement = document.createElementNS(
			"http://www.w3.org/2000/svg",
			"title"
		);

		svgTitleElement.textContent = 'Non Workable Day';

		nonWorkableTaskHighlightTemplate.appendChild(svgTitleElement)

		gantt._ganttChartNotWorkableDayTaskHighlightTemplate = nonWorkableTaskHighlightTemplate;


		/**
		 * Build task delay indicator template
		 */

		gantt._ganttChartTaskDelayIndicatorTemplate = createElement(`
			<div class="task-delay-indicator cursor-help rounded">
				<i class="mdi mdi-alert-rhombus"></i>
			</div>
		`);

		// Init scroll sync
		requestAnimationFrame(syncStickyScrollPositions);
	}

	function initModals() {
		// Constraints modal
		gantt._taskConstraintsModal = createTaskConstraintsModal(gantt);

		// Refresh tasks when constraints get updated
		gantt._taskConstraintsModal.addSuccessListener(() => {
			invalidateTaskDependants(gantt._taskConstraintsModal.currentTask, true)
			fullRefresh();
		})

		// Workable days modal
		gantt._workableDaysModal = createWorkableDaysModal(gantt);

		gantt._workableDaysModal.addSuccessListener(() => {
			// Do this on a timeout so the modal hiding animation
			// happens before the full refresh
			setTimeout(() => {
				// Recalculate non workable days
				clearWorkableDaysCache();
				recalculateEndDates();
				fullRefresh();
			})
		})

		// Constraints modal
		gantt._taskDelaysModal = createTaskDelaysModal(gantt);

		// Refresh tasks when delays get updated
		gantt._taskDelaysModal.addSuccessListener(() => {
			const task = gantt._taskDelaysModal.currentTask;
			// Update task duration from delay if task requires it
			if (taskAffectedByDelays(task)) {
				setTaskStartDate(task, getCurrentStartDate(task), true);
			}
		})

		// Display task Modal
		gantt._displayTaskModal = createDisplayTaskModal(gantt);

		// Init as null
		gantt._printModal = null;
	}

	/**
	 * This is run for all clicks on the container before all other custom and
	 * base handlers
	 *
	 * @param {MouseEvent} event
	 */
	function containerClickHandler(event) {
		// Save click position
		gantt._lastClickX = event.clientX;
		gantt._lastClickY = event.clientY;

		// If we have a popup open...
		if (gantt._lastTaskPopupWrapper) {
			if (
				// ... and we clicked anywhere but inside it ...
				!gantt._lastTaskPopupWrapper.contains(event.target)
			) {
				// ... hide it
				hideTaskPopup();
			}
		}
	}

	/**
	 * Click handler for the document, to keep track of actions
	 * outside of the main container
	 *
	 * @param {MouseEvent} event
	 */
	function documentClickHandler(event) {
		unselectTasksIfNeeded(event);
	}

	/**
	 * @param {MouseEvent} event
	 */
	function unselectTasksIfNeeded(event) {
		let target = event.target;

		// Check if we clicked outside the container
		const containerClass = '.gantt-wrapper-container';

		if (target.closest(containerClass) === null) {
			// Click outside gantt container
			unselectAllTasks();
		} else {
			// Click inside gantt container, check if we should preserve selection or not
			// If we clicked inside any of the following containers, preserve task selections
			const containersWhitelist = [
				'toolbar-container', // Tool bar
				'ag-row' // Any row
			];

			let preserveTaskSelection = false

			// Traverse clicked element parents
			while (target) {
				for (const className of containersWhitelist) {
					// Check if clicked element inside allowed container
					if (target.classList.contains(className)) {
						preserveTaskSelection = true;
						break;
					}
				}

				target = target.parentElement;
			}

			// If we didn't click on any white listed container, remove
			// current task selection
			if (preserveTaskSelection === false) {
				unselectAllTasks();
			}
		}
	}

	function overrideExistingMethods() {
		// noinspection JSUndefinedPropertyAssignment
		gantt._baseRender = gantt.render;

		addQuarterViewMode(gantt);

		gantt.render = function () {
			beforeRenderHook();
			this._baseRender();
			afterRenderHook();
		};

	}

	/**
	 * Fully refreshes the gantt chart, including sorting the tasks again
	 * @param {GanttWrapper.RefreshOptions} options
	 */
	function fullRefresh(options = {}) {
		if (gantt._wrapperOptions.viewOnly) {
			return viewOnlyFullRefresh();
		}

		// Default editor behavior
		if (gantt._refreshing) {
			// Wait until previous refresh has finished
			setTimeout(() => fullRefresh(), 0);
			return;
		}

		clearCache();
		gantt._invalidateDependantsIdHistory = [];
		gantt._calculateConstraintsIdHistory = [];
		gantt._refreshing = true;

		// Sort tasks before rendering
		const sortedTasks = getSortedTasks();

		// Pre process tasks
		sortedTasks.forEach(task => {
			// Clear task errors
			if (task.errors.length) {
				// Clear errors
				task.errors = [];
				// But make sure they are calculated again
				invalidateTaskDependants(task, true);
			}

			if (options.preserveHiddenStatus !== true) {
				// Clear hidden tasks
				task.hidden = false;
			}
		})

		if (options.preserveHiddenStatus !== true) {
			// Hide collapsed children
			sortedTasks.filter(task => task.collapsed)
				.forEach(collapsedTask => {
					traverseDescendants(collapsedTask, childTask => {
						childTask.hidden = true;
					})
				})
		}

		// Update constraints
		sortedTasks
			.filter(task => {
				return task.needs_dates_calculation;
			})
			.forEach(task => {
				try {
					calculateTasksDerivedValues(task);
				} catch (e) {
					// Ignore any error
				}
			})

		// Clear finaldx from frappe gantt internal bars property
		// Sometimes when dragging, it seems to get stuck and any future
		// click on the SVG will repeat the previous drag motion, so we
		// manually clear it here to prevent it from happening
		if (gantt.bars) {
			// Clear frappe gantt bars
			gantt.bars.forEach(bar => {
				bar.$bar.finaldx = 0;
			})
		}

		// Display only non hidden tasks
		gantt.refresh(sortedTasks.filter(task => task.hidden === false));
	}

	/**
	 * Fully refreshes the gantt chart, for view only editor version
	 */
	function viewOnlyFullRefresh() {
		if (gantt._refreshing) {
			// Wait until previous refresh has finished
			setTimeout(() => fullRefresh(), 0);
			return;
		}

		clearCache();
		gantt._invalidateDependantsIdHistory = [];
		gantt._calculateConstraintsIdHistory = [];
		gantt._refreshing = true;

		// Sort tasks before rendering
		const sortedTasks = getSortedTasks();

		// Pre process tasks
		sortedTasks.forEach(task => {
			// Clear task errors
			if (task.errors.length) {
				// Clear errors
				task.errors = [];
				// But make sure they are calculated again
				invalidateTaskDependants(task, true);
			}

			// Clear hidden tasks
			task.hidden = false;
		})

		// Hide collapsed children
		sortedTasks.filter(task => task.collapsed)
			.forEach(collapsedTask => {
				traverseDescendants(collapsedTask, childTask => {
					childTask.hidden = true;
				})
			})

		// Clear finaldx from frappe gantt internal bars property
		// Sometimes when dragging, it seems to get stuck and any future
		// click on the SVG will repeat the previous drag motion, so we
		// manually clear it here to prevent it from happening
		if (gantt.bars) {
			// Clear frappe gantt bars
			gantt.bars.forEach(bar => {
				bar.$bar.finaldx = 0;
			})
		}

		// Display only non hidden tasks
		gantt.refresh(sortedTasks.filter(task => task.hidden === false));
	}

	/**
	 * Returns the gantt task sorted so parent tasks are always above child tasks
	 * @return {import('./ganttWrapper').GanttWrapper.Task[]}
	 */
	function getSortedTasks() {
		const sortedTasks = [];

		// Get root tasks
		const rootTasks = gantt._all_tasks.filter(task => {
			return task.dependencies.length === 0;
		})

		rootTasks.forEach(rootTask => {
			sortedTasks.push(rootTask);

			// Add all descendants before moving to next root task
			traverseDescendants(rootTask, descendantTask => {
				sortedTasks.push(descendantTask);
			})
		})

		// We also update the all tasks array with the new sorted array
		// so the grid can make use of it later when rendering the rows
		gantt._all_tasks = sortedTasks;

		return sortedTasks;
	}

	/**
	 * Traverses sequentially through all parents of the given tasks
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @param {function(import('./ganttWrapper').GanttWrapper.Task):void} callback
	 */
	function traverseParents(task, callback) {
		if (task.dependencies.length) {
			// Retrieve parent task, we assume a task will only have 1 parent
			const parentTask = findTaskById(task.dependencies[0]);

			callback(parentTask);

			traverseParents(parentTask, callback);
		}
	}

	/**
	 * Traverses sequentially through all descendants of the given tasks
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @param {function(import('./ganttWrapper').GanttWrapper.Task):void} callback
	 */
	function traverseDescendants(task, callback) {
		// Find direct descendants
		const descendants = gantt._all_tasks.filter(_task => {
			return _task.dependencies.includes(task.id);
		})

		// Traverse them
		descendants.forEach(childTask => {
			callback(childTask);

			traverseDescendants(childTask, callback)
		})
	}

	function beforeRenderHook() {
		// Save scroll position before rendering
		gantt._lastScrollTop = gantt._ganttContainer.scrollTop;

		if (gantt._preserveRelativePositionAfterRender) {
			const currentScrollLeft = gantt._ganttContainer.scrollLeft
			const maxScrollLeft = gantt._ganttContainer.scrollWidth - gantt._ganttContainer.clientWidth;

			// Store percentage of total scroll left
			gantt._lastScrollLeft = currentScrollLeft / maxScrollLeft;
		} else {
			// Store absolute value of scroll left
			gantt._lastScrollLeft = gantt._ganttContainer.scrollLeft;
		}
	}

	function afterRenderHook() {
		// Only update gantt stuff if we actually need it
		if (gantt._editorViewType !== 'timesheet') {
			copySVGPatternsToGanttSVG();
			positionTitleScreen();
			initArrowsHandler()
			initAnchorPoints();
			setupNonWorkDaysHighlights();
			setupDelayedDaysHighlights();
			setupDelaysIndicators();
			setupBottomToolsContainer();
			setupGanttVerticalPaddingContainer();
		}

		setupTasksPanel();

		// Only update gantt stuff if we actually need it
		if (gantt._editorViewType !== 'timesheet') {
			setupStickyDuplicatedDates();
			setupConstraintInteractionLocks();

			// Reset vertical anchor bar left position
			// We need to do this otherwise the fake scrollbar will take
			// into account the position of the anchor guide and calculate
			// the wrong max width

			// noinspection JSUnresolvedReference
			gantt._verticalAnchorGuide.style.left = '0px';

			setupGanttChartFakeScrollContainer();
		}

		// Update print button status
		gantt._printToolbarButton.disabled = getTasks().length === 0;

		// Restore scroll position after rendering
		gantt._ganttContainer.scrollTop = gantt._lastScrollTop;

		if (gantt._preserveRelativePositionAfterRender) {
			// Use relative value
			const maxScrollLeft = gantt._ganttContainer.scrollWidth - gantt._ganttContainer.clientWidth;

			gantt._ganttContainer.scrollLeft = gantt._lastScrollLeft * maxScrollLeft;
		} else {
			// Use absolute value
			gantt._ganttContainer.scrollLeft = gantt._lastScrollLeft;
		}

		// Clear flag
		gantt._preserveRelativePositionAfterRender = false;

		// Notify any listener/callback
		if (gantt._wrapperOptions.onTasksChange) {
			gantt._wrapperOptions.onTasksChange(gantt._getTasks())
		}
	}

	/**
	 * Scroll the inner container so that current day is centered on the screen
	 */
	function scrollInnerContainer() {
		const container = gantt._ganttContainer;

		const todayHighlightElement = container.querySelector('.today-highlight');

		container.scrollLeft =
			parseInt(todayHighlightElement.getAttribute('x')) - (container.clientWidth / 2);
	}

	/**
	 * Copy SVG pattern from the templates container so they
	 * can be referenced from the gantt SVG
	 */
	function copySVGPatternsToGanttSVG() {
		// Retrieve patterns container
		const svgPatterns = domElement.querySelector('.templates svg g.patterns');

		// Paste on gantt SVG container
		gantt._svgContainer.appendChild(svgPatterns.cloneNode(true))

	}

	/**
	 * Places a div to cover the title bar, preventing user interaction
	 */
	function positionTitleScreen() {
		if (!gantt._titleBarScreen) {
			// Not fully initialized
			return;
		}
		// Retrieve title bar element
		const titleBarSVG = domElement.querySelector('.bar-wrapper[data-id="-1"]');

		// Assign title class
		titleBarSVG.classList.add('title');

		const boundingRect = getRelativePositionFromContainer(titleBarSVG);

		// Update title screen position to cover title bar
		// noinspection JSUnresolvedVariable
		gantt._titleBarScreen.style.left = `${boundingRect.x}px`;
		// noinspection JSUnresolvedVariable
		gantt._titleBarScreen.style.top = `${boundingRect.y}px`;
		// noinspection JSUnresolvedVariable
		gantt._titleBarScreen.style.width = `${boundingRect.width}px`;
		// noinspection JSUnresolvedVariable
		gantt._titleBarScreen.style.height = `${boundingRect.height}px`;

		// Offset gantt container by the first bar height, completely hiding title bar
		gantt._ganttContainer.style.top = `-${getFirstBarLocation().height}px`;
	}

	/**
	 * Initializes the arrows pointing to relationships
	 */
	function initArrowsHandler() {
		if (gantt._wrapperOptions.viewOnly) {
			// Disable editing relationships directly
			return;
		}
		const arrowsContainer = gantt._container.querySelector('.arrow');
		arrowsContainer.classList.add('editable');
		gantt._container.querySelectorAll('.arrow path').forEach(path => {
				// Create custom id to make it easy to keep track
				// of the path pairs
				path.dataset.pathId = `${path.dataset.to}-${path.dataset.from}`;

				// Duplicate path so we have something that can be easily selected
				const fakeWiderPath = path.cloneNode(true);

				// Assign path classes
				path.classList.add('real-path');
				fakeWiderPath.classList.add('hover-only-path');

				// Add svg title attribute
				const svgTitle = document.createElementNS(
					'http://www.w3.org/2000/svg',
					'title'
				);
				svgTitle.innerHTML = 'Remove Link';
				fakeWiderPath.appendChild(svgTitle);

				const throttledShowIconFunction = throttle(
					event => {
						showRemoveRelationshipIconOnPath(path, event);
					},
					16, // 60~ fps
					{
						leading: true
					},
				);

				// Setup events
				fakeWiderPath.addEventListener('mouseenter', event => {
					// noinspection JSCheckFunctionSignatures
					throttledShowIconFunction(event)
				})
				fakeWiderPath.addEventListener('mousemove', event => {
					// noinspection JSCheckFunctionSignatures
					throttledShowIconFunction(event)
				})

				fakeWiderPath.addEventListener('mouseleave', () => hideRemoveRelationshipIconOnPath(path))
				fakeWiderPath.addEventListener('mouseout', () => hideRemoveRelationshipIconOnPath(path))

				fakeWiderPath.addEventListener('click', () => removePathRelationship(path));

				// Add to arrows container
				arrowsContainer.appendChild(fakeWiderPath);
			}
		)
	}

	/**
	 * Initializes the date anchor points, drawing one for
	 * each date between the first and last drawn by the base frappe gantt instance
	 * using its own get_snap_position() to calculate their position
	 */
	function initAnchorPoints() {
		// Clear existing points
		gantt._dateAnchorPointsContainer.innerHTML = '';
		gantt._dateAnchorsCacheByPosition = {};
		gantt._dateAnchorsCacheByDate = {};

		// Remove container from DOM while we add the anchors
		gantt._ganttContainer.removeChild(gantt._dateAnchorPointsContainer);

		// Resize container to match SVG
		const svgWidth = gantt._ganttContainer.querySelector('svg').getBoundingClientRect().width;
		// noinspection JSUnresolvedVariable
		gantt._dateAnchorPointsContainer.style.width = `${svgWidth}px`;

		// We won't append it directly to the element until the end
		// for performance reasons
		let anchorPointsHtml = '';

		if (gantt.dates) {
			// Render new points
			const firstDate = moment(gantt.dates[0]);
			const lastDate = moment(gantt.dates[gantt.dates.length - 1]);

			// We divide the length of the container with the number of days
			// it has so we get a delta we can use to retrieve the snap points
			const daysBetween = lastDate.diff(firstDate, 'days');

			const delta = svgWidth / daysBetween;

			let currentPosition = 0;
			const currentDate = firstDate;
			while (currentDate.isBefore(lastDate)) {
				const snapPosition = gantt.get_snap_position(currentPosition);

				// Create anchor point
				const anchorPointHTML = `
					<div
						class="anchor-point"
						data-x="${snapPosition}"
						data-date="${formatDateForGanttChart(currentDate)}"
						style="left: ${snapPosition}px; top: 4px"
					>
					</div>
				`;

				anchorPointsHtml = anchorPointsHtml.concat(anchorPointHTML);

				const anchorDate = moment(currentDate);

				gantt._dateAnchorsCacheByPosition[snapPosition] = {
					xPosition: snapPosition,
					date: anchorDate,
					width: delta,
					getElement() {
						return gantt._ganttContainer.querySelector(`.anchor-point[data-x="${snapPosition}"]`)
					}
				}

				// Populate cache by date
				gantt._dateAnchorsCacheByDate[formatDateForGanttChart(anchorDate)] = gantt._dateAnchorsCacheByPosition[snapPosition];

				currentPosition = snapPosition + delta;
				currentDate.add(1, 'days');
			}
		}

		// Add HTML to element
		gantt._dateAnchorPointsContainer.innerHTML = anchorPointsHtml;

		// Add back anchors container to DOM
		gantt._ganttContainer.appendChild(gantt._dateAnchorPointsContainer);
	}

	/**
	 * Adds non work days highlights
	 */
	function setupNonWorkDaysHighlights() {
		// Only add timeline highlights if we are on day mode
		if (gantt.options.view_mode === 'Day') {
			const lastBarLocation = getLastBarLocation();

			// Get all days on the timeline using the already setup anchor points
			Object.values(gantt._dateAnchorsCacheByPosition).forEach(dateAnchor => {
				if (isWorkDay(dateAnchor.date) === false) {
					// Highlight as non workable date
					const highlight = gantt._ganttChartNotWorkableDayTimelineHighlightTemplate
						.cloneNode(true);

					const ganttGrid = gantt._svgContainer.querySelector('.grid');

					// Position highlight
					highlight.setAttribute('height', lastBarLocation.bottom);
					highlight.setAttribute('x', dateAnchor.xPosition);
					highlight.setAttribute('y', 0);
					highlight.setAttribute('width', dateAnchor.width);

					ganttGrid.appendChild(highlight);

					// Also add task highlights
					getVisibleTasks().forEach(task => {
						const startDate = getCurrentStartDate(task);
						const endDate = getCurrentEndDate(task);

						if (
							startDate.isBefore(dateAnchor.date, 'day') &&
							endDate.isSameOrAfter(dateAnchor.date, 'day')
						) {
							const barContainer = getTaskRelatedBarElement(task);
							const actualBar = barContainer.querySelector('.bar');

							const taskHighlight = gantt._ganttChartNotWorkableDayTaskHighlightTemplate
								.cloneNode(true);

							// Position task highlight
							taskHighlight.setAttribute('height', actualBar.getAttribute('height'));
							taskHighlight.setAttribute('x', dateAnchor.xPosition);
							taskHighlight.setAttribute('y', actualBar.getAttribute('y'));
							taskHighlight.setAttribute('width', dateAnchor.width);

							// Add to bar wrapper, before text label
							const textLabel = barContainer.querySelector('.bar-group .bar-label');
							barContainer.querySelector('.bar-group').insertBefore(
								taskHighlight,
								textLabel
							);
						}
					})
				}
			})
		}
	}

	function setupDelayedDaysHighlights() {
		getVisibleTasks().forEach(task => {
			if (taskAffectedByDelays(task) === false) {
				// Skip task
				return;
			}

			// Go through each day for a task and check if it should be
			// highlighted as delayed
			const startDate = getCurrentStartDate(task).clone();
			const endDate = getCurrentEndDate(task).clone();

			task.delays.forEach(delay => {
				const delayDate = delay.startDate;

				// If delay has no date, skip it
				if (!delayDate) {
					return;
				}

				// If delay date is not within task start & end dates
				// we can't render the highlights
				if (delayDate.isBefore(startDate, 'day') || delayDate.isAfter(endDate, 'day')) {
					return;
				}

				const totalDelayedDays = delay.duration;
				let currentDelayedDays = 0;

				const currentDay = startDate.clone();

				// Check each day of the task to highlight days
				// affected by the delay
				do {
					// Is a workable day and is after the delay date
					if (isWorkDay(currentDay) && currentDay.isSameOrAfter(delayDate, 'day')) {
						const dateAnchor = gantt._dateAnchorsCacheByDate[
							formatDateForGanttChart(currentDay)
							];

						if (!dateAnchor) {
							console.error(
								'Missing date anchor for date during task delay highlighting',
								currentDay
							)

							break;
						}

						// Add delay highlight
						const barContainer = getTaskRelatedBarElement(task);
						const actualBar = barContainer.querySelector('.bar');

						const taskHighlight = gantt._ganttChartNotWorkableDayTaskHighlightTemplate
							.cloneNode(true);

						taskHighlight.classList.add('delayed-day');
						taskHighlight.querySelector('title').innerHTML = delay.reason;

						// Position task highlight
						taskHighlight.setAttribute('height', actualBar.getAttribute('height'));
						taskHighlight.setAttribute('x', dateAnchor.xPosition);
						taskHighlight.setAttribute('y', actualBar.getAttribute('y'));
						taskHighlight.setAttribute('width', dateAnchor.width);

						// Add to bar wrapper, before text label
						const textLabel = barContainer.querySelector('.bar-group .bar-label');
						barContainer.querySelector('.bar-group').insertBefore(
							taskHighlight,
							textLabel
						);

						currentDelayedDays += 1;

						if (currentDelayedDays === totalDelayedDays) {
							// Highlighted all delayed days for this delay, stop
							// checking
							break;
						}
					}

					// Check next day
					currentDay.add(1, 'day');
				} while (currentDay.isSameOrBefore(endDate, 'day'))
			})
		})
	}

	function setupDelaysIndicators() {
		// Clear previous indicators
		gantt._ganttContainer.querySelectorAll('.task-delay-indicator').forEach(indicator => {
			indicator.remove()
		})

		getVisibleTasks().forEach(task => {
			if (taskAffectedByDelays(task) === false) {
				// Skip task
				return;
			}

			// Go through each day for a task and check if it should be
			task.delays.forEach(delay => {
				const delayDate = delay.startDate;

				// If delay has no date, skip it
				if (!delayDate) {
					return;
				}

				// Find related date anchor
				const relatedDateAnchor = gantt._dateAnchorsCacheByDate[formatDateForGanttChart(delayDate)];

				// Skip if related date anchor not found (may be outside rendered area)
				if (!relatedDateAnchor) {
					return;
				}

				const barContainer = getTaskRelatedBarElement(task);

				// Create indicator
				const indicatorElement = gantt._ganttChartTaskDelayIndicatorTemplate.cloneNode(true);

				// Center vertically on bar
				const barPosition = getRelativePositionFromContainer(barContainer);
				const barMiddle = barPosition.top + (barPosition.height / 2);
				indicatorElement.style.top = barMiddle + 'px';
				// Center horizontally on anchor
				const anchorPosition = relatedDateAnchor.xPosition;
				indicatorElement.style.left = anchorPosition + 'px';

				indicatorElement.title = delay.reason;

				indicatorElement.addEventListener('click', () => {
					if (gantt._wrapperOptions.viewOnly) {
						openViewTaskModal(task);
					} else {
						showTaskDelaysModal(task);
					}
				});

				gantt._ganttContainer.appendChild(indicatorElement);
			})
		})
	}

	function setupGanttVerticalPaddingContainer() {
		// Make padding start right after gantt chart ends
		const ganttHeight = gantt._ganttContainer.getBoundingClientRect().height;
		const ganttVerticalOffset = gantt._ganttContainer.offsetTop;

		const paddingTop = ganttHeight + ganttVerticalOffset;

		gantt._ganttChartDragPaddingContainer.style.top = `${paddingTop}px`;
	}

	function setupBottomToolsContainer() {
		const lastBarLocation = getLastBarLocation();
		const svgWidth = gantt._ganttContainer.querySelector('svg').getBoundingClientRect().width;

		// noinspection JSUnresolvedVariable
		gantt._bottomToolsContainer.style.top = `${lastBarLocation.bottom}px`;
		// noinspection JSUnresolvedVariable
		gantt._bottomToolsContainer.style.width = `${svgWidth}px`;

		// Save top & bottom values for later use
		// noinspection JSUnresolvedVariable
		gantt._bottomToolsContainer.dataset.top = lastBarLocation.bottom;
		// noinspection JSUnresolvedVariable
		gantt._bottomToolsContainer.dataset.bottom = lastBarLocation.bottom +
			gantt._bottomToolsContainer.getBoundingClientRect().height;
	}

	/**
	 * Initializes the toolbar and its contents
	 */
	function initToolbar() {
		/**
		 * Add tool bar container
		 */
		const printButtonVisibility = gantt._wrapperOptions.forAdminLayout ?
			'data-hide-if-view-only="false"' :
			'data-hide-if-view-only="true"';

		gantt._toolBarContainer = createElement(`
			<div
				class="toolbar-container"
			>
				<button
					type="button"
					class="btn btn-success btn-sm icon-button save-timeline-button"
					title="Save Timeline"
					data-hide-if-view-only="true"
			  	>
					<span class="mdi mdi-content-save"></span>
				</button>
				<button
					type="button"
					class="btn btn-primary btn-sm add-task-button"
					data-hide-if-view-only="true"
			  	>
					Add Task
				</button>
				<button
					type="button"
					class="btn btn-light btn-sm icon-button move-up-button"
					title="Move Task Up"
					data-hide-if-view-only="true"
				>
					<span class="mdi mdi-chevron-up"></span>
				</button>
				<button
					type="button"
					class="btn btn-light btn-sm icon-button move-down-button"
					title="Move Task Down"
					data-hide-if-view-only="true"
				>
					<span class="mdi mdi-chevron-down"></span>
				</button>
				<button
					type="button"
					class="btn btn-light btn-sm icon-button outdent-button"
					title="Outdent Task"
					data-hide-if-view-only="true"
				>
					<span class="mdi mdi-format-horizontal-align-left"></span>
				</button>
				<button
					type="button"
					class="btn btn-light btn-sm icon-button indent-button"
					title="Indent Task"
					data-hide-if-view-only="true"
				>
					<span class="mdi mdi-format-horizontal-align-right"></span>
				</button>
				<button
					type="button"
					class="btn btn-light btn-sm icon-button collapse-button"
					title="Collapse All"
					data-hide-if-view-only="true"
				>
					<span class="mdi mdi-arrow-collapse-vertical"></span>
				</button>
				<button
					type="button"
					class="btn btn-light btn-sm icon-button expand-button"
					title="Expand All"
					data-hide-if-view-only="true"
				>
					<span class="mdi mdi-arrow-expand-vertical"></span>
				</button>
				<button
					type="button"
					class="btn btn-light btn-sm icon-button enable-printable-flag-button"
					title="Enable Printing For All"
					data-hide-if-view-only="true"
				>
					<span class="mdi mdi-printer-check"></span>
				</button>
				<button
					type="button"
					class="btn btn-light btn-sm icon-button disable-printable-flag-button"
					title="Disable Printing For All"
					data-hide-if-view-only="true"
				>
					<span class="mdi mdi-printer-off"></span>
				</button>
				<div class="white-space-spacer"></div>
				<button
					type="button"
					class="btn btn-light btn-sm icon-button print-button"
					title="Print Gantt"

					${printButtonVisibility}

				>
					<span class="mdi mdi-printer"></span>
				</button>
				<select
					class="form-control form-control-sm editor-view-type-select"
					data-hide-if-view-only="true"
				>
					<option value="split">Split View</option>
					<option value="timesheet">Timesheet View</option>
					<option value="gantt">Gantt View</option>
				</select>

				<select
					class="form-control form-control-sm view-type-select"
				>
					<option value="Day">Gantt Day View</option>
					<option value="Week">Gantt Week View</option>
					<option value="Month">Gantt Month View</option>
					<option value="Quarter">Gantt Quarter View</option>
				</select>

				<button
				 	type="button"
					class="btn btn-secondary btn-sm manage-working-days-button"
					data-hide-if-view-only="true"
				>
					Set Working Days
				</button>
			</div>
		`);

		/** Init Save Timeline Button */
		gantt._toolBarContainer
			.querySelector('.save-timeline-button')
			.addEventListener('click', () => handleSaveRequest())

		/** Init Add Tasks Button */
		gantt._toolBarContainer
			.querySelector('.add-task-button')
			.addEventListener('click', () => addTaskFromToolbar())

		/** Init indent tasks buttons */
		gantt._toolBarContainer
			.querySelector('.outdent-button')
			.addEventListener('click', () => outdentSelectedTasks())
		gantt._toolBarContainer
			.querySelector('.indent-button')
			.addEventListener('click', () => indentSelectedTasks())

		/** Init move up and down buttons */
		gantt._toolBarContainer
			.querySelector('.move-up-button')
			.addEventListener('click', () => moveSelectedTasksUp())
		gantt._toolBarContainer
			.querySelector('.move-down-button')
			.addEventListener('click', () => moveSelectedTasksDown())

		/** Init collapse/expand buttons */
		gantt._toolBarContainer
			.querySelector('.collapse-button')
			.addEventListener('click', () => collapseTasks())
		gantt._toolBarContainer
			.querySelector('.expand-button')
			.addEventListener('click', () => expandTasks())

		/** Init collapse/expand buttons */
		gantt._toolBarContainer
			.querySelector('.enable-printable-flag-button')
			.addEventListener('click', () => enablePrintableFlagForTasks())
		gantt._toolBarContainer
			.querySelector('.disable-printable-flag-button')
			.addEventListener('click', () => disablePrintableFlagForTasks())

		/** Init print button */
		gantt._printToolbarButton = gantt._toolBarContainer
			.querySelector('.print-button');

		gantt._printToolbarButton.addEventListener('click', () => openPrintModal());

		/** Init View Select */

		gantt._viewTypeSelect = gantt._toolBarContainer.querySelector('.view-type-select');

		gantt._viewTypeSelect.addEventListener('change', handleViewTypeChange);

		/** Init Editor View Select */

		gantt._editorViewTypeSelect = gantt._toolBarContainer.querySelector('.editor-view-type-select');

		gantt._editorViewTypeSelect.addEventListener('change', handleEditorViewTypeChange);

		/** Init Manage Working Days Button */

		gantt._toolBarContainer
			.querySelector('.manage-working-days-button')
			.addEventListener('click', () => displayManageWorkingDaysModal())


		gantt._container.insertBefore(gantt._toolBarContainer, gantt._container.firstChild);
	}

	/**
	 * Initializes the task panel grid that will display the entries
	 */
	function initTaskPanelGrid() {
		const firstBar = getFirstBarLocation();

		const debouncedRowDataUpdateHandler = _.throttle(
			() => {
				// Make columns as large as required to fit content
				const gridInstance = /** @type import('../../../../helpers/grids/grids').GridInstance */ (gantt._taskPanelGridInstance);

				// Make columns as large as required to fit content
				gridInstance.columnApi.autoSizeAllColumns();

				/** Because we auto-sized the columns, we need to update sticky container to match the new width */
				// Update headers
				setupTaskPanelFakeHeaders();

				// Update fake scrollbars
				setupTasksPanelFakeScrollContainer();

				// Restore selected tasks & cell
				restoreSelectedTasks();
				restoreLastFocusedCell();

				// Mark refreshing as finished, after the grid has been updated
				gantt._refreshing = false;
			},
			33,
			{
				trailing: true,
				leading: false,
			}
		);

		gantt._taskPanelGridInstance = createGrid(
			gantt._taskPanelEntriesContainer,
			{
				rowHeight: firstBar.height,
				pagination: false,
				readOnlyEdit: true,
				popupParent: gantt._viewport,
				rowSelection: 'multiple',
				rowMultiSelectWithClick: false,
				onCellEditRequest: handleCellEditRequest,
				enableCellTextSelection: false,
				paginationPageSize: 1500,
				cacheBlockSize: 2000,
				suppressColumnVirtualisation: true,
				getRowClass: params => {
					return customGetRowClass(params);
				},
				onDragStarted: onDragStart,
				onDragStopped: onDragStopped,
				onRowDragEnd: onTaskRowDragEnd,
				onRowDragMove: _.throttle(onTaskRowDragMove, 8),
				suppressDragLeaveHidesColumns: true,
				onColumnMoved() {
					// Update fake headers
					setupTaskPanelFakeHeaders();
				},
				onColumnResized() {
					setupTaskPanelFakeHeaders();
				},
				columnDefs: [
					{
						headerName: '#',
						field: 'id',
						editable: false,
						cellClass: 'fill-space',
						cellRenderer: createCellRenderer(params => {
							return renderRowIndexCell(params)
						}),
						maxWidth: 50,
						rowDragText: params => {
							return params.rowNode.data.name;
						},
						lockPosition: 'left',
					},
					{
						headerName: '',
						field: 'id',
						editable: false,
						cellClass: 'fill-space',
						cellRenderer: createCellRenderer(params => {
							return renderIndentControlsCell(params, params.data)
						}),
						maxWidth: 54, // Magic Number
						lockPosition: 'left',
					},
					{
						headerName: '',
						field: 'id',
						editable: false,
						cellClass: 'fill-space',
						cellRenderer: createCellRenderer(params => {
							return renderPrintableFlagCell(params, params.data)
						}),
						maxWidth: 30, // Magic Number
						lockPosition: 'left',
					},
					{
						headerName: 'Task Name',
						field: 'name',
						editable: true,
						cellClass: 'fill-space',
						minWidth: 200,
						cellRenderer: createCellRenderer(params => {
							return renderTaskNameCell(params, params.data);
						}),
						lockPosition: 'left',
						resizable: true,
					},
					{
						headerName: 'Start Date',
						field: 'start',
						cellRenderer: createCellRenderer(params => {
							return renderTaskStartDateCell(params, params.data)
						}),
						editable: params => {
							return canEditStartDate(params.data);
						},
						cellEditor: CustomDateCellEditor,
						cellEditorParams: {
							dateField: '_start',
						},
						suppressMovable: false,
						resizable: true,
					},
					{
						headerName: 'Duration',
						field: 'duration',
						valueGetter: (params) => {
							return params.data.duration;
						},
						cellRenderer: createCellRenderer(params => {
							return renderTaskDuration(params, params.data)
						}),
						maxWidth: 100, // Magic Number
						editable: params => {
							return canEditEndDate(params.data);
						},
						suppressMovable: false,
						resizable: true,
					},
					{
						headerName: 'End Date',
						field: 'end',
						cellRenderer: createCellRenderer(params => {
							return renderTaskEndDateCell(params, params.data)
						}),
						editable: params => {
							return canEditEndDate(params.data);
						},
						cellEditor: CustomDateCellEditor,
						cellEditorParams: {
							dateField: '_end',
							isEndDate: true,
						},
						suppressMovable: false,
						resizable: true,
					},
					{
						headerName: 'Constraints',
						field: 'constraints',
						cellRenderer: createCellRenderer(params => {
							return renderConstraintsCell(params, params.data)
						}),
						maxWidth: 200, // Magic Number
						cellEditor: CustomConstraintsCellEditor,
						cellEditorParams: {
							ganttInstance: gantt,
						},
						editable: params => {
							return isParentTask(params.data) === false;
						},
						suppressMovable: false,
						resizable: true,
					},
					{
						headerName: 'Delays',
						field: 'id',
						cellRenderer: createCellRenderer(params => {
							return renderDelaysCell(params, params.data)
						}),
						onCellDoubleClicked: params => {
							if (isParentTask(params.data) === false) {
								showTaskDelaysModal(params.data);
							}
						},
						maxWidth: 200, // Magic Number
						editable: false,
						suppressMovable: false,
						resizable: true,
					},
					{
						headerName: 'Progress',
						field: 'progress',
						editable: params => {
							return canEditProgress(params.data);
						},
						cellEditor: 'agNumberCellEditor',
						cellRenderer: createCellRenderer(params => {
							return renderTaskProgress(params, params.data)
						}),
						maxWidth: 100,
						cellEditorParams: {
							min: 0,
							max: 100,
							precision: 0,
							step: 1,
						},
						suppressMovable: false,
						resizable: true,
					},
				],
				onGridReady() {
					const gridInstance = /** @type import('../../../../helpers/grids/grids').GridInstance */ (gantt._taskPanelGridInstance);

					// Setup popup parent
					gridInstance.api.setPopupParent(gantt._viewportWrapper);

					// Make columns as large as required to fit content
					gridInstance.columnApi.autoSizeAllColumns();

					// Grow grid as much as possible at the start
					gantt._taskPanelWidth = getTasksPanelMaxWidth();

					fullRefresh();
				},
				onRowDataUpdated() {
					debouncedRowDataUpdateHandler();
				},
				onRowSelected(event) {
					updateToolbarForTaskSelections();

					if (
						event.source === 'rowDataChanged' ||
						event.source === 'api'
					) {
						// Skip event if coming from an API action
						// IE, events triggered by restoring selected rows
						return;
					}

					storeSelectedTasks();
				},
				isExternalFilterPresent() {
					// External filter always active
					return true;
				},
				doesExternalFilterPass(node) {
					/** @var {GanttWrapper.Task} */
					const task = node.data;

					return task.hidden === false;
				}
			},
			() => {
				return gantt._all_tasks.slice(1)
			}
		)

		// Setup a recurring function to further initialize rows
		setInterval(initUninitializedTaskRows, 128) // Speed is not that important here
	}

	/**
	 * We use ag grid getRowClass to add a custom class and the task id as a class
	 * so we can later do some custom initialization of each row
	 *
	 * @param {import('ag-grid-community/dist/lib/entities/gridOptions').RowClassParams} params
	 * @return {string[]}
	 */
	function customGetRowClass(params) {
		if (!params || !params.data) {
			// Return early if the grid/row is not ready
			return [''];
		}

		const rowClasses = [
			'task-row',
			`task-row-task-id-${params.data.id}`, // Add the task id as a class so we can retrieve it later
		];

		/**
		 * @type {GanttWrapper.Task}
		 */
		const task = params.data;

		if (isParentTask(task)) {
			rowClasses.push('parent');
		}

		if (task.collapsed) {
			rowClasses.push('collapsed');
		}

		task.errors.forEach(taskError => {
			if (taskError.type === 'error') {
				rowClasses.push('task-error');
			} else if (taskError.type === 'warning') {
				rowClasses.push('task-warning');
			}
		})

		if (task.is_milestone) {
			rowClasses.push('task-milestone');
		}

		return rowClasses;
	}

	/**
	 * Because ag grid does not give us a hook into the row DOM element
	 * and we can't just retrieve all rows after a refresh because the may not be
	 * rendered yet due to virtualization, we need to constantly check for uninitialized rows
	 * and do it ourselves
	 */
	function initUninitializedTaskRows() {
		// Get uninitialized rows on DOM
		const uninitializedRows = Array.from(
			gantt._taskPanelContainer
				.querySelectorAll('.task-row:not([data-init])')
		);

		uninitializedRows.forEach(rowDomNode => {
			const relatedTask = retrieveRelatedTaskFromRowDomNode(rowDomNode);

			// Setup right click handler
			rowDomNode.addEventListener(
				'contextmenu',
				event => handleTaskRowContextMenu(event, relatedTask),
				{
					// We want to handle it before ag-grid can
					capture: true,
				}
			);

			// Mark as initialized
			rowDomNode.dataset.init = 'true';
		})
	}

	/**
	 * Display custom right click menu for tasks panel grid rows right click
	 *
	 * @param {MouseEvent} event
	 * @param {import('./ganttWrapper').GanttWrapper.Task} relatedTask
	 */
	function handleTaskRowContextMenu(event, relatedTask) {
		// Create fake click event with original click position
		const clickEvent = new MouseEvent('click', {
			clientX: event.clientX,
			clientY: event.clientY
		});

		// Get related bar DOM element for the task on the gantt chart
		const relatedBarElement = getTaskRelatedBarElement(relatedTask);

		// Send fake even to display popup
		relatedBarElement.dispatchEvent(clickEvent);

		// Prevent ag-grid from handling this
		event.stopPropagation();
		event.preventDefault();
	}

	/**
	 * @param {HTMLElement} rowNode
	 * @return {import('./ganttWrapper').GanttWrapper.Task}
	 */
	function retrieveRelatedTaskFromRowDomNode(rowNode) {
		let taskId = undefined;

		for (const classListElement of rowNode.classList) {
			// Find class containing the task id
			if (/task-row-task-id-.*/.test(classListElement)) {
				const stringParts = classListElement.split('-');

				// Retrieve id from class string
				taskId = stringParts[stringParts.length - 1];
				break;
			}
		}

		// This should never happen
		if (taskId === undefined) {
			console.error('Related task not found for row node', rowNode);
		}

		return findTaskById(taskId);
	}

	/**
	 * @param {any} params
	 */
	function renderRowIndexCell(params) {
		const cell = createElement(`
			<div class="task-cell task-index-cell cursor-move">
				${getTaskDisplayIndex(params.data)}
			</div>
		`);

		// Register the whole cell as a row dragger
		params.registerRowDragger(cell);

		return cell;
	}

	/**
	 * @param {any} params
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 */
	function renderIndentControlsCell(params, task) {
		const canIndent = getPrecedingSiblingTasks(task).length > 0;
		const canOutdent = task.dependencies.length > 0;

		const cell = createElement(`
			<div class="task-cell task-indent-controls-cell">
				<button
					class="btn btn-sm btn-light w-auto p-0 outdent-button"
					title="${canOutdent ? 'Outdent' : "Can't outdent this task any further"}"
				 	${canOutdent ? '' : 'disabled'}
				>
					<span class="mdi mdi-chevron-up" style="font-size: 1.6rem"></span>
				</button>
				<button
				 	class="btn btn-sm btn-light w-auto p-0 indent-button"
				 	title="${canIndent ? 'Indent' : "Can't indent this task any further"}"
				 	${canIndent ? '' : 'disabled'}
				>
					<span class="mdi mdi-chevron-up" style="font-size: 1.6rem"></span>
				</button>
			</div>
		`)

		cell.querySelector('.outdent-button').addEventListener('click', () => {
			outdentTask(task);
		})

		cell.querySelector('.indent-button').addEventListener('click', () => {
			indentTask(task);
		})

		return cell;
	}

	/**
	 * @param {any} params
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 */
	function renderPrintableFlagCell(params, task) {
		let title;
		let partiallyPrintable = false;
		let notPrintableDueToParent = false;

		if (task.printable) {
			if (task.printable) {
				title = 'This task will be visible on public reports';

				if (isTaskFullyPrintable(task) === false) {
					title = 'Some of the sub tasks belonging to this task are not visible on public reports';
					partiallyPrintable = true;
				}
				if (isTaskNotPrintableDueToParent(task)) {
					title = 'A parent task is not printable preventing this task from being visible';
					notPrintableDueToParent = true;
				}
			}
		} else {
			title = 'This task will not be visible on public reports';
		}

		const cell = createElement(`
			<div
				class="
					task-cell task-printable-flag-cell
					${(task.printable && notPrintableDueToParent === false) ? 'enabled' : ''}
					${partiallyPrintable ? 'partially' : ''}
					${notPrintableDueToParent ? 'not-printable-due-to-parent' : ''}
					"
				title="${title}"
			>
				<span class="mdi mdi-printer"></span>
			</div>
		`)

		cell.addEventListener('click', () => {
			toggleTaskPrintableStatus(task, undefined, true);
		})

		return cell;
	}

	/**
	 * @param {any} params
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 */
	function renderTaskNameCell(params, task) {
		// Get number of parents so we can add the required indents
		const parents = getTaskParents(task);

		let indentsHtml = '';

		parents.forEach(() => {
			indentsHtml += '<div class="indent"></div>'
		})

		const warnings = task.errors.filter(error => error.type === 'warning');
		const errors = task.errors.filter(error => error.type === 'error');

		let warning = '';
		let error = '';
		let isMilestoneIcon = ''

		if (errors.length) {
			error = `
				<div class="error" title="${errors[0].getDisplayValueForError()}">
					<span class="mdi mdi-close-circle" style="font-size: 1rem"></span>
				</div>
			`
		} else if (warnings.length) {
			// Only check for warnings if no errors were found
			warning = `
				<div class="warning" title="${warnings[0].getDisplayValueForError()}">
					<span class="mdi mdi-alert" style="font-size: 1rem"></span>
				</div>
			`;
		}

		if (task.is_milestone) {
			isMilestoneIcon = `
				<div class="is-milestone-icon" title="Milestone">
					<span class="mdi mdi-flag-variant" style="font-size: 1rem"></span>
				</div>
			`;
		}

		const isParent = isParentTask(task);


		const taskCell = createElement(`
			<div class="task-cell task-name-cell">
				${warning}
				${error}
				<div class="indents">${indentsHtml}</div>
				<div class="list-decoration ${isParent ? 'cursor-pointer' : ''} ${task.collapsed ? 'collapsed' : ''}"></div>
				<div>
					${task.name}
				</div>
				${isMilestoneIcon}
			</div>
		`)

		if (isParent) {
			// Init collapse toggle
			const collapseToggle = taskCell.querySelector('.list-decoration');

			collapseToggle.addEventListener('click', () => {
				toggleTaskCollapsedStatus(task);
			});
		}

		return taskCell;
	}

	/**
	 * @param {any} params
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 */
	function renderTaskStartDateCell(params, task) {
		const formattedDate = moment(task._start).format('MM/DD/YYYY');

		const canEditCell = canEditStartDate(task);

		let title = '';
		if (isParentTask(task)) {
			title = 'Derived from sub tasks';
		} else if (hasStartDateConstraints(task)) {
			title = 'Derived from constraints'
		}

		return createElement(`
			<div
				title="${title}"
				class="task-cell task-start-date-cell ${canEditCell ? '' : 'not-editable'}"
			>
				${formattedDate}
			</div>
		`)
	}

	/**
	 * @param {any} params
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 */
	function renderTaskEndDateCell(params, task) {
		const formattedDate = getCurrentEndDate(task)
			.format('MM/DD/YYYY');

		const canEditCell = canEditEndDate(task);

		let title = '';
		if (isParentTask(task)) {
			title = 'Derived from sub tasks';
		} else if (hasStartDateConstraints(task)) {
			title = 'Derived from constraints'
		}

		return createElement(`
			<div
				title="${title}"
				class="task-cell task-end-date-cell ${canEditCell ? '' : 'not-editable'}"
			>
				${formattedDate}
			</div>
		`)
	}

	/**
	 * @param {any} params
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 */
	function renderTaskDuration(params, task) {
		const duration = getTaskActualDuration(task);
		const delayedDays = getDelayedDays(task);

		const canEditCell = canEditEndDate(task);

		let title = '';
		if (isParentTask(task)) {
			title = ' - Derived from sub tasks';
		} else if (hasEndDateConstraints(task)) {
			title = ' - Derived from constraints'
		}

		if (delayedDays > 0) {
			return createElement(`
				<div
					class="task-cell task-duration-cell ${canEditCell ? '' : 'not-editable'}"
					title="Delayed By ${delayedDays} ${delayedDays > 1 ? 'Days' : 'Day'}"
				>
					${duration - delayedDays}
					<span class="text-warning">
						+${delayedDays}
					</span>
					${duration > 1 ? 'Days' : 'Day'}
				</div>
			`)
		} else {
			return createElement(`
				<div
					class="task-cell task-duration-cell ${canEditCell ? '' : 'not-editable'}"
					title="In Workable Days ${canEditCell ? '' : title}"
				>
					${duration} ${duration > 1 ? 'Days' : 'Day'}
				</div>
			`)
		}
	}

	/**
	 * @param {any} params
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 */
	function renderConstraintsCell(params, task) {
		if (task.constraints.length > 0) {
			const constraintsString = getTaskConstraintsTextRepresentation(task);

			let styles = '';
			let title = getTaskConstraintsTextDescription(task);
			let mutedClass = '';

			if (isParentTask(task)) {
				styles += 'text-decoration: line-through;'
				title = "Constraints don't affect parent tasks";
				mutedClass = 'text-muted';
			}

			return createElement(`
				<div
					class="task-cell task-duration-cell ${mutedClass}"
					title="${title}"
					style="${styles}"
				>
					${constraintsString}
				</div>
			`)
		} else {
			return createElement(`
				<div
					class="task-cell task-duration-cell text-muted"
					title="No Constraints Set For This Task"
				>
					None
				</div>
			`)
		}
	}

	/**
	 * @param {any} params
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 */
	function renderDelaysCell(params, task) {
		let delaysCount = task.delays.length;
		if (delaysCount > 0) {

			const title = delaysCount > 1 ?
				`${delaysCount} Delays` : '1 Delay';

			const ignoreDelays = taskAffectedByDelays(task) === false;

			return createElement(`
				<div
					class="task-cell task-delays-cell ${ignoreDelays ? 'text-muted' : ''}"
					title="${title}"
					style="${ignoreDelays ? 'text-decoration: line-through' : ''}"
				>
					${delaysCount}
				</div>
			`)
		} else {
			return createElement(`
				<div
					class="task-cell task-delays-cell text-muted"
					title="No Constraints Set For This Task"
				>
					None
				</div>
			`)
		}
	}


	/**
	 * @param {any} params
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 */
	function renderTaskProgress(params, task) {
		const canEditCell = canEditProgress(task);

		return createElement(`
			<div
				class="task-cell task-progress-cell ${canEditCell ? '' : 'not-editable'}"
				title="${canEditCell ? '' : 'Derived from sub tasks'}"
			>
				${task.progress}%
			</div>
		`)
	}

	/**
	 * @param {import('ag-grid-community/dist/lib/events').CellEditRequestEvent} event
	 */
	function handleCellEditRequest(event) {
		const gridInstance = /** @type import('../../../../helpers/grids/grids').GridInstance */ (gantt._taskPanelGridInstance);

		// Keep track of last focused cell
		gantt._lastFocusedGridCell = gridInstance.api.getFocusedCell();

		/** @type {import('ganttWrapper').GanttWrapper.Task} */
		const task = event.data;

		const editedField = event.column.getColId();

		if (editedField === 'name') {
			setTaskName(task, event.newValue);
		} else if (editedField === 'start') {
			setTaskStartDate(task, moment(event.newValue, 'MM/DD/YYYY'));
		} else if (editedField === 'end') {
			setTaskEndDate(task, moment(event.newValue, 'MM/DD/YYYY'));
		} else if (editedField === 'progress') {
			setTaskProgress(task, event.newValue);
		} else if (editedField === 'duration') {
			setTaskDuration(task, event.newValue);
		} else if (editedField === 'constraints') {
			setTaskConstraints(task, event.newValue)
		} else {
			console.error(event);
			throw 'Unhandled cell edit request event';
		}
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 */
	function showTaskConstraintsModal(task) {
		gantt._taskConstraintsModal.setTask(task);
		gantt._taskConstraintsModal.display(true);
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 */
	function showTaskDelaysModal(task) {
		gantt._taskDelaysModal.display(task, true);
	}

	function setupTaskPanelFakeHeaders() {
		const fakeHeadersViewport = gantt._taskPanelHeaderContainer.querySelector('.ag-header-viewport');

		const originalHeaders = gantt._taskPanelContainer.querySelector('.ag-header-container');

		const newHeaders = originalHeaders.cloneNode(true);

		fakeHeadersViewport.children[0].replaceWith(newHeaders);

		// Sync style attributes
		const allOriginalNodes = Array.from(originalHeaders.querySelectorAll('*'));
		const newHeaderNodes = Array.from(newHeaders.querySelectorAll('*'));

		newHeaders.setAttribute('style', originalHeaders.getAttribute('style'));
		allOriginalNodes.forEach((ogNode, index) => {
			newHeaderNodes[index].setAttribute('style', ogNode.getAttribute('style'));
		})

		// Redirect click events to handle column dragging & resizing
		const newCellHeaders = Array.from(newHeaders.querySelectorAll('.ag-header-cell'));
		const originalCellHeaders = Array.from(originalHeaders.querySelectorAll('.ag-header-cell'));

		newCellHeaders.forEach((cellHeader, index) => {
			// Retrieve original header
			const originalHeader = originalCellHeaders[index];

			// Redirect mouse events for column dragging
			redirectMouseMoveEvents(cellHeader, originalHeader)
			// Redirect mouse events for column resizing
			const resizeHandleElement = cellHeader.querySelector('.ag-header-cell-resize');
			if (resizeHandleElement) {
				// Redirect from fake cell to real cell
				redirectMouseMoveEvents(
					resizeHandleElement,
					originalHeader.querySelector('.ag-header-cell-resize'),
					true,
				);
			}
		});

		// Copy width from panel
		gantt._taskPanelHeaderContainer.style.width = gantt._taskPanelContainer.style.width;
		// Copy height from duplicated dates container from gantt chart for symmetry
		gantt._taskPanelHeaderContainer.style.height = `${gantt._ganttDatesDuplicatedStickyHeaderContainer.getBoundingClientRect().height}px`;

	}

	function setupTasksPanelFakeScrollContainer() {
		// Setup fake scrollbar container width
		gantt._taskPanelScrollbarContainer.style.width = gantt._taskPanelContainer.style.width;

		const gridHorizontalScrollContainer = gantt._taskPanelContainer.querySelector('.ag-center-cols-viewport');

		const entriesScrollWidth = gridHorizontalScrollContainer.scrollWidth;

		// Sync task panel fake scrollbar width to match entries container scroll width
		gantt._taskPanelScrollbarContainer.querySelector('.padding').style.width = `${entriesScrollWidth}px`;
	}

	function setupGanttChartFakeScrollContainer() {
		// Setup fake scrollbar container width
		gantt._ganttChartScrollbarContainer.style.width = gantt._ganttContainer.getBoundingClientRect().width + 'px';

		// Position scrollbar based on task panel fake scrollbar
		gantt._ganttChartScrollbarContainer.style.left = gantt._taskPanelScrollbarContainer.style.width;

		// Clear scroll left to get an accurate reading of scrollWidth
		gantt._ganttContainer.scrollLeft = 0;

		const ganttScrollWidth = gantt._ganttContainer.scrollWidth;

		// Sync task panel fake scrollbar width to match real container container scroll width
		const fakePaddingElement = gantt._ganttChartScrollbarContainer.querySelector('.padding');
		fakePaddingElement.style.width = `${ganttScrollWidth}px`;
	}

	/**
	 * Handles positioning task in the task panel after each render
	 */
	function setupTasksPanel() {
		// Set panel width
		gantt._taskPanelContainer.style.width = `${gantt._taskPanelWidth}px`;

		const firstBarLocation = getFirstBarLocation();

		// Set panel height
		const viewPortHeight = gantt._viewport.getBoundingClientRect().height + firstBarLocation.height;
		const ganttChartHeight = gantt._ganttContainer.getBoundingClientRect().height + firstBarLocation.height;
		const panelHeight = ganttChartHeight > viewPortHeight ? ganttChartHeight : viewPortHeight;

		gantt._taskPanelContainer.style.height = `${panelHeight}px`;

		// Offset gantt position to match panel width
		gantt._ganttContainer.style.marginLeft = `${gantt._taskPanelWidth}px`;

		// Also Offset gantt vertical padding container to match panel width
		gantt._ganttChartDragPaddingContainer.style.left = `${gantt._taskPanelWidth}px`;


		// Update grid instance
		const gridInstance = /** @type {import('../../../../helpers/grids/grids').GridInstance} */ (gantt._taskPanelGridInstance);

		// Copy height from duplicated dates container from gantt chart for symmetry
		gridInstance.api.setHeaderHeight(gantt._ganttDatesDuplicatedStickyHeaderContainer.getBoundingClientRect().height - 2);

		// Redraw tasks
		gridInstance._silentRefresh();

		setupTasksPanelFakeScrollContainer();
	}

	/**
	 * Returns the relative position of an element compared with
	 * the parent gantt-container
	 *
	 * @param {Element} element
	 * @return {DOMRect}
	 */
	function getRelativePositionFromContainer(element) {
		const elementBoundingRect = element.getBoundingClientRect();

		return getRelativePositionFromGanttContainer(elementBoundingRect)
	}

	/**
	 * Returns the relative position of a bounding box compared with
	 * the parent gantt-container bounding box
	 *
	 * @param {DOMRect} box
	 * @return {DOMRect}
	 */
	function getRelativePositionFromGanttContainer(box) {
		const containerBoundingRect = gantt._ganttContainer.getBoundingClientRect();

		return new DOMRect(
			box.x - containerBoundingRect.x + gantt._ganttContainer.scrollLeft,
			box.y - containerBoundingRect.y + gantt._ganttContainer.scrollTop,
			box.width,
			box.height,
		);
	}

	/**
	 * Returns the relative position of a bounding box compared with
	 * the parent viewport bounding box
	 *
	 * @param {DOMRect} box
	 * @return {DOMRect}
	 */
	function getRelativePositionFromViewport(box) {
		const containerBoundingRect = gantt._viewport.getBoundingClientRect();

		return new DOMRect(
			box.x - containerBoundingRect.x + gantt._viewport.scrollLeft,
			box.y - containerBoundingRect.y + gantt._viewport.scrollTop,
			box.width,
			box.height,
		);
	}

	/**
	 * Fit an absolutely positioned (with inline pixel values for top/left) element inside the viewport/container
	 *
	 * @param {Element} element
	 */
	function fitInsideContainer(element) {
		const containerBox = gantt._container.getBoundingClientRect();
		const elementBox = element.getBoundingClientRect();

		// Margin in pixels when moving the container inside the viewport
		const margin = 24;

		const topOffset = containerBox.y - elementBox.y;
		const rightOffset = containerBox.right - elementBox.right;
		const bottomOffset = containerBox.bottom - elementBox.bottom;
		const leftOffset = containerBox.x - elementBox.x;

		// Retrieve left and top pixel values from inline style

		// noinspection JSUnresolvedVariable
		const elementLeft = element.style.left ?
			parseFloat(element.style.left.split('px')[0]) : 0;

		// noinspection JSUnresolvedVariable
		const elementTop = element.style.top ?
			parseFloat(element.style.top.split('px')[0]) : 0;

		// Element is hidden from the top
		if (topOffset > 0) {
			// Shift element
			// noinspection JSUnresolvedVariable
			element.style.top = `${elementTop + topOffset + margin}px`;
		}

		// Element is hidden from the right
		if (rightOffset < 0) {
			// Shift element
			// noinspection JSUnresolvedVariable
			element.style.left = `${elementLeft + rightOffset - margin}px`;
		}

		// // Element is hidden from the bottom
		if (bottomOffset < 0) {
			// Shift element
			// noinspection JSUnresolvedVariable
			element.style.top = `${elementTop + bottomOffset - margin}px`;
		}

		// Element is hidden from the left
		if (leftOffset > 0) {
			// Shift element
			// noinspection JSUnresolvedVariable
			element.style.left = `${elementLeft + leftOffset + margin}px`;
		}
	}

	function setupStickyDuplicatedDates() {
		const duplicatedSvg = gantt._ganttDatesDuplicatedStickyHeaderContainer.querySelector('svg');

		// Set position & width of container
		gantt._ganttDatesDuplicatedStickyHeaderContainer.style.left = `${gantt._taskPanelWidth}px`;
		gantt._ganttDatesDuplicatedStickyHeaderContainer.style.width = `${gantt._ganttContainer.getBoundingClientRect().width}px`;

		// Copy SVG width...
		duplicatedSvg.setAttribute(
			'width',
			gantt._svgContainer.getAttribute('width')
		);
		// ... & header's height
		duplicatedSvg.setAttribute(
			'height',
			gantt._svgContainer.querySelector('.grid-header').getAttribute('height')
		);

		// Clear previous nodes
		duplicatedSvg.innerHTML = '';

		// Clone nodes if they exist
		const nodesToClone = [
			'.grid-header',
			'.date',
			'.today-highlight',
			'.non-workable-day-timeline-highlight'
		]

		nodesToClone.forEach(nodeClass => {
			gantt._svgContainer.querySelectorAll(nodeClass).forEach(node => {
				duplicatedSvg.appendChild(
					node.cloneNode(true)
				);
			});
		});
	}

	/**
	 * We need to constantly keep the scroll position of certain elements synced,
	 * we do it here for:
	 * 		- Fake duplicated dates container in sync with the gantt chart horizontal scroll position
	 * 		- Task Panel Fake Scrollbar synced with the entries horizontal scroll position
	 * 		- Gantt Chart Fake Scrollbar synced with the chart horizontal scroll position
	 * 		- Task Panel Headers synced with the entries horizontal scroll position
	 */
	function syncStickyScrollPositions() {
		// Sync gantt dates header
		gantt._ganttDatesDuplicatedStickyHeaderContainer.scrollLeft = gantt._ganttContainer.scrollLeft;

		// Sync task panel fake scrollbar
		const taskPanelGridHorizontalScrollContainer = gantt._taskPanelContainer.querySelector('.ag-center-cols-viewport');

		if (gantt._taskPanelNativeScrolling) {
			// Sync fake scrollbar against entries
			gantt._taskPanelScrollbarContainer.scrollLeft = taskPanelGridHorizontalScrollContainer.scrollLeft;
		} else {
			// Sync entries against fake scrollbar
			taskPanelGridHorizontalScrollContainer.scrollLeft = gantt._taskPanelScrollbarContainer.scrollLeft;
		}

		// Sync gantt chart fake scrollbar
		if (gantt._ganttChartNativeScrolling) {
			// Sync fake scrollbar against entries
			gantt._ganttChartScrollbarContainer.scrollLeft = gantt._ganttContainer.scrollLeft;
		} else {
			// Sync entries against fake scrollbar
			gantt._ganttContainer.scrollLeft = gantt._ganttChartScrollbarContainer.scrollLeft;
		}

		// Sync task panel header if already rendered
		const taskPanelHeaderEntries = gantt._taskPanelHeaderContainer
			.querySelector('.ag-header-container');
		if (taskPanelHeaderEntries) {
			taskPanelHeaderEntries
				.style
				.transform = `translateX(-${taskPanelGridHorizontalScrollContainer.scrollLeft}px)`;
		}

		// Continue syncing
		requestAnimationFrame(syncStickyScrollPositions);
	}

	function handleViewTypeChange() {
		const newView = gantt._viewTypeSelect.value;

		gantt._preserveRelativePositionAfterRender = true;

		gantt.change_view_mode(newView);
	}

	function setViewType(newType, withFullRefresh = false) {
		gantt._preserveRelativePositionAfterRender = true;

		gantt.change_view_mode(newType);

		if (withFullRefresh) {
			fullRefresh();
		}
	}

	function handleEditorViewTypeChange() {
		const previousView = gantt._editorViewType;

		// noinspection JSValidateTypes -- We know it will match
		gantt._editorViewType = gantt._editorViewTypeSelect.value;

		if (previousView === 'split') {
			// Store last panel width
			gantt._lastTaskPanelWidth = gantt._taskPanelWidth;
		}

		const splitViewAnchor = gantt._taskPanelContainer.querySelector('.task-panel-width-anchor');

		switch (gantt._editorViewType) {
			case "split":
				// Restore task panel width
				gantt._taskPanelWidth = gantt._lastTaskPanelWidth;
				splitViewAnchor.classList.remove('d-none');
				gantt._viewTypeSelect.disabled = false;
				break;
			case "timesheet":
				gantt._taskPanelWidth = gantt._viewport.getBoundingClientRect().width;
				splitViewAnchor.classList.add('d-none');
				gantt._viewTypeSelect.disabled = true;
				break;
			case "gantt":
				gantt._taskPanelWidth = 0;
				splitViewAnchor.classList.add('d-none');
				gantt._viewTypeSelect.disabled = false;
				break;
		}

		fullRefresh();
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {string}
	 */
	function getCustomTaskPopup(task) {
		if (gantt._wrapperOptions.viewOnly) {
			// Ignore default popup, open a read only modal
			setTimeout(() => {
				openViewTaskModal(task);
			})
			return '';
		}

		const popupId = task.id + _.random(0, 100000);

		// Wait until dom is attached to initialize contents
		waitUntil(() => {
			return gantt._ganttContainer.querySelector(`.details-container[data-popup-id="${popupId}"]`);
		}).then(() => {
			initCustomTaskPopup(task, gantt._ganttContainer.querySelector(`.details-container[data-popup-id="${popupId}"]`))
		})

		// Return HTML string so frappe gantt attaches it to document
		return `
			<div class="details-container opacity-0 pointer-events-none custom shadow-sm" data-popup-id="${popupId}">
				<div class="d-flex">
					<div class="form-group my-0 flex-grow-1">
						<div class="d-flex align-items-start">
							<label class="my-0">Task Name</label>
							<button class="btn btn-sm btn-light ml-auto w-auto p-0 move-task-up-button" title="Move Task Up">
								<span class="mdi mdi-chevron-up" style="font-size: 1.6rem"></span>
							</button>
							<button class="btn btn-sm btn-light ml-1 w-auto p-0 move-task-down-button" title="Move Task Down">
								<span class="mdi mdi-chevron-down" style="font-size: 1.6rem"></span>
							</button>
						</div>
						<input
							type="text"
							class="form-control form-control-sm"
							autocomplete="off"
							name="task_name"
							value="${task.name}"
						>
					</div>
				</div>
			  	<div class="form-group mt-1 mb-0">
					<label class="d-flex my-0">
						<div>
							Progress
						</div>
						<div class="ml-auto text-muted">
							<span class="task-progress-label mr-1">${task.progress}</span>%
						</div>
					</label>
					<input
						type="range"
						class="form-control-range"
						name="task_range"
						min="0"
						max="100"
						step="1"
						value="${task.progress}"
						${isParentTask(task) ? 'disabled' : ''}
					>
				</div>
				<div class="form-check task-milestone-checkbox-container cursor-pointer mb-0" title="Milestone tasks will appear in the project calendar">
					<input
						class="form-check-input task-milestone-input ml-0 my-0 cursor-pointer"
						style="left: 4px; top: 4px"
						type="checkbox"
						${task.is_milestone ? 'checked' : ''}
					 >
					<label class="form-check-label cursor-pointer">
						Mark As Milestone
					</label>
				</div>
					<div class="form-check task-printable-checkbox-container cursor-pointer" title="If disabled, tasks will be hidden from prints and customer views">
					<input
						class="form-check-input task-milestone-input ml-0 my-0 cursor-pointer"
						style="left: 4px; top: 4px"
						type="checkbox"
						${task.printable ? 'checked' : ''}
					 >
					<label class="form-check-label cursor-pointer">
						Visible By Customer
					</label>
				</div>
				<div class="container mt-1">
					<div class="row">
						<button
							type="button"
							class="btn btn-sm btn-primary col add-task-above-button d-flex align-items-center justify-content-center position-relative"
						>
							<span class="mdi mdi-table-row-plus-before mdi-18px position-absolute" style="left: 8px"></span>
							Add Task Above
						</button>
					</div>
					<div class="row mt-1">
						<button
							type="button"
							class="btn btn-sm btn-primary col add-task-below-button d-flex align-items-center justify-content-center position-relative"
						>
							<span class="mdi mdi-table-row-plus-after mdi-18px position-absolute" style="left: 8px"></span>
							Add Task Below
						</button>
					</div>
				</div>
				<div class="container">
					<div class="mt-1 row">
						<button
							type="button"
							class="btn btn-sm btn-info col constraints-button"
							${isParentTask(task) ? 'disabled' : ''}
						>
							Constraints
						</button>
						<button
							type="button"
							class="btn btn-sm btn-info col ml-1 delays-button"
							${isParentTask(task) ? 'disabled' : ''}
						>
							Delays
						</button>
					</div>
				</div>
				<div class="mt-1 d-flex">
					<button
						type="button"
						class="btn btn-sm btn-success flex-grow-1 add-child-button"
					>
						Add Child
					</button>
					<button
						type="button"
						class="btn btn-sm btn-danger ml-2 w-auto delete-task-button"
						style="font-size: 1.2rem"
						title="This will remove all child tasks"
					>
						<span class="mdi mdi-delete"></span>
					</button>
				</div>
			</div>
	  `;
	}

	function openViewTaskModal(task) {
		gantt._displayTaskModal.display(task, true);
	}

	function openPrintModal() {
		// Use modal as template and duplicate it
		const modalTemplate = document.querySelector('.modals .timeline-print-modal');

		/** @var {Element} */
		const modalInstance = modalTemplate.cloneNode(true);

		modalTemplate.parentElement.appendChild(modalInstance);

		const printModal = new TimelinePrintModal({
			modalContainer: modalInstance,
			gantt,
			timelineName: gantt.__timeline_name,
		});

		printModal.display();
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @param {HTMLElement} popupElement
	 */
	function initCustomTaskPopup(task, popupElement) {
		// Position element on last click location
		const newPopupLocation = getRelativePositionFromViewport(
			new DOMRect(
				gantt._lastClickX,
				gantt._lastClickY - 20, // Move a bit above the cursor
				0,
				0
			),
		)

		let popupWrapper = popupElement.parentElement;

		// Hide original wrapper still on the gantt container
		popupWrapper.style.opacity = '0';
		popupWrapper.classList.add('pointer-events-none');

		// Clone and move popup node to the viewport, so it can
		// be displayed above the tasks panel too
		const clonedWrapper = popupWrapper.cloneNode(true);

		// Use the cloned wrapper popup element, no the original
		popupElement = clonedWrapper.querySelector('.details-container ');

		const previousClonedWrapper = gantt._viewportWrapper.querySelector('.viewport > .popup-wrapper')

		// Remove previous popup
		if (previousClonedWrapper) {
			previousClonedWrapper.parentElement.removeChild(previousClonedWrapper);
		}

		// Append to viewport
		gantt._viewport.appendChild(clonedWrapper);

		// noinspection JSUnresolvedVariable
		clonedWrapper.style.left = `${newPopupLocation.x}px`;
		// noinspection JSUnresolvedVariable
		clonedWrapper.style.top = `${newPopupLocation.y}px`;

		// // Make sure the popup is visible inside the viewport/container
		fitInsideContainer(clonedWrapper);

		// Save popup to wrapper instance
		gantt._lastTaskPopupWrapper = clonedWrapper;

		// Init task name input
		const nameInput = popupElement.querySelector('input[name="task_name"]');
		nameInput.addEventListener('keyup', debounce(() => {
			setTaskName(task, nameInput.value);
		}, 64))

		// Init delete button
		const deleteButton = popupElement.querySelector('.delete-task-button');
		if (deleteButton) {
			deleteButton.addEventListener('click', () => removeTask(task));
		}

		// Init add child button
		const addChildButton = popupElement.querySelector('.add-child-button');
		if (addChildButton) {
			addChildButton.addEventListener('click', () => addChildTask(task));
		}

		// Init delay & constraints buttons
		const constraintsButton = popupElement.querySelector('.constraints-button');
		if (constraintsButton) {
			constraintsButton.addEventListener(
				'click', () => {
					hideTaskPopup();
					showTaskConstraintsModal(task);
				}
			);
		}
		const delaysButton = popupElement.querySelector('.delays-button');
		if (delaysButton) {
			delaysButton.addEventListener(
				'click', () => {
					hideTaskPopup();
					showTaskDelaysModal(task);
				}
			);
		}

		// Init link buttons
		const linkToParentButton = popupElement.querySelector('.add-task-above-button');
		if (linkToParentButton) {
			linkToParentButton.addEventListener('click', () => addNewTaskAbove(task));
		}
		const linkToChildButton = popupElement.querySelector('.add-task-below-button');
		if (linkToChildButton) {
			linkToChildButton.addEventListener('click', () => addNewTaskBelow(task));
		}

		// Init move task buttons
		const moveTaskUpButton = popupElement.querySelector('.move-task-up-button');
		if (moveTaskUpButton) {
			moveTaskUpButton.addEventListener('click', () => moveTaskUp(task));
		}

		const moveTaskDownButton = popupElement.querySelector('.move-task-down-button');
		if (moveTaskDownButton) {
			moveTaskDownButton.addEventListener('click', () => moveTaskDown(task));
		}

		// Init progress slider
		const progressSlider = popupElement.querySelector('input[name="task_range"]');
		if (progressSlider) {
			const debouncedUpdate = debounce(() => {
				// Update popup value
				const progressLabel = progressSlider.parentElement
					.querySelector('.task-progress-label');

				progressLabel.innerHTML = progressSlider.value;

				setTaskProgress(task, parseInt(progressSlider.value));
			}, 64);

			progressSlider.addEventListener('change', debouncedUpdate);
			progressSlider.addEventListener('input', debouncedUpdate);
		}

		// Init milestone checkbox
		// We need to manually toggle the checkbox because using the
		// browser's htmlFor attribute to do it by clicking on the label
		// seems to cause issues
		const milestoneCheckboxContainer = popupElement.querySelector('.task-milestone-checkbox-container');
		const milestoneCheckbox = milestoneCheckboxContainer.querySelector('input');
		const milestoneCheckBoxLabel = milestoneCheckboxContainer.querySelector('label');
		if (milestoneCheckboxContainer) {
			// Handle click on label
			milestoneCheckBoxLabel.addEventListener('click', e => {
				e.preventDefault();
				e.stopPropagation()

				// Manually toggle checkbox
				milestoneCheckbox.checked = !milestoneCheckbox.checked;

				// Update task
				setTaskMilestoneStatus(task, milestoneCheckbox.checked);
			})
			// Handle click on input
			milestoneCheckbox.addEventListener('change', () => {
				setTaskMilestoneStatus(task, milestoneCheckbox.checked);
			})
		}

		// Init printable checkbox
		// We need to manually toggle the checkbox because using the
		// browser's htmlFor attribute to do it by clicking on the label
		// seems to cause issues
		const printableCheckboxContainer = popupElement.querySelector('.task-printable-checkbox-container');
		const printableCheckbox = printableCheckboxContainer.querySelector('input');
		const printableCheckBoxLabel = printableCheckboxContainer.querySelector('label');
		if (printableCheckboxContainer) {
			// Handle click on label
			printableCheckBoxLabel.addEventListener('click', e => {
				e.preventDefault();
				e.stopPropagation()

				// Manually toggle checkbox
				printableCheckbox.checked = !printableCheckbox.checked;

				// Update task
				toggleTaskPrintableStatus(task, printableCheckbox.checked);
			})
			// Handle click on input
			printableCheckbox.addEventListener('change', () => {
				toggleTaskPrintableStatus(task, printableCheckbox.checked);
			})
		}

		// Focus name input
		nameInput.focus();
		nameInput.select();

		// Clear wrapper if it was hidden
		clonedWrapper.classList.remove('opacity-0')
		clonedWrapper.classList.remove('pointer-events-none')
		clonedWrapper.style.opacity = '1'; // Need to update inline style

		// Finally display popup
		popupElement.classList.remove('opacity-0')
		popupElement.classList.remove('pointer-events-none')
	}

	/**
	 * Visually hide the task popup, we let the base frappe gantt instance
	 * handle removing it when needed
	 */
	function hideTaskPopup() {
		const popupWrapper = gantt._lastTaskPopupWrapper;
		if (popupWrapper) {
			// Hide wrapper
			popupWrapper.classList.add('opacity-0')
			popupWrapper.classList.add('pointer-events-none')
			// noinspection JSUnresolvedVariable
			popupWrapper.style.opacity = '0'; // Need to update inline style

			// We also move it to the start of the container to prevent any scroll issues
			// noinspection JSUnresolvedReference
			popupWrapper.style.top = '0px';
			// noinspection JSUnresolvedReference
			popupWrapper.style.left = '0px';

			// Also clear variable from wrapper instance
			gantt._lastTaskPopupWrapper = undefined;
		}
	}

	/**
	 * @param {string} taskId
	 * @return {import('./ganttWrapper').GanttWrapper.Task|undefined}
	 */
	function findTaskById(taskId) {
		return gantt._all_tasks.find(task => {
			return task.id === taskId;
		})
	}

	/**
	 * Finds a task by its display index
	 *
	 * @param {string|number} taskIndex
	 * @return {import('./ganttWrapper').GanttWrapper.Task|undefined}
	 */
	function findTaskByIndex(taskIndex) {
		const displayedTasks = getTasks();

		if (typeof taskIndex === 'string') {
			taskIndex = parseInt(taskIndex);
		}

		for (const _task of displayedTasks) {
			const index = displayedTasks.indexOf(_task);
			if (index + 1 === taskIndex) {
				return _task;
			}
		}

		return undefined;
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {number}
	 */
	function getTaskIndex(task) {
		return gantt._all_tasks.findIndex(_task => {
			return _task.id === task.id;
		})
	}

	/**
	 * Returns the grid instance API for the task panel
	 *
	 * This function works as an IDE helper to get the proper type
	 */
	function getGridApi() {
		const gridInstance = /** @type import('../../../../helpers/grids/grids').GridInstance */ (gantt._taskPanelGridInstance);

		return gridInstance.api;
	}

	/**
	 * Returns the grid column API for the task panel
	 *
	 * This function works as an IDE helper to get the proper type
	 */
	function getColumnApi() {
		const gridInstance = /** @type import('../../../../helpers/grids/grids').GridInstance */ (gantt._taskPanelGridInstance);

		return gridInstance.columnApi;
	}

	/**
	 * Returns selected rows on the task panel
	 * @return {GanttWrapper.Task[]}
	 */
	function getSelectedTasks() {
		const rows = getGridApi().getSelectedRows();

		return rows.map(row => findTaskById(row.id)) // Map row to task
			.filter(task => task); // Filter not found task ids
	}

	/**
	 * Unselects all tasks from the task panel
	 */
	function unselectAllTasks() {
		getGridApi().getSelectedNodes().forEach(node => {
			node.setSelected(false, false);
		})
	}

	/**
	 * Returns all the parents of a task all the way to the root
	 *
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {import('./ganttWrapper').GanttWrapper.Task[]}
	 */
	function getTaskParents(task) {
		const parents = [];

		traverseParents(task, parentTask => {
			parents.push(parentTask);
		})

		return parents;
	}

	/**
	 * Check if a task is a parent task
	 *
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {boolean}
	 */
	function isParentTask(task) {
		return withCache(
			task,
			'is-parent-task',
			() => {
				return getChildTasks(task).length > 0;
			}
		);
	}

	/**
	 * Check if a task start date can be edited
	 *
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {boolean}
	 */
	function canEditStartDate(task) {
		return withCache(
			task,
			'can-edit-start-date',
			() => {
				return isParentTask(task) === false && hasStartDateConstraints(task) === false;
			}
		);
	}

	/**
	 * Check if a task start date can be edited
	 *
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {boolean}
	 */
	function canEditEndDate(task) {
		return withCache(
			task,
			'can-edit-end-date',
			() => {
				return isParentTask(task) === false && hasEndDateConstraints(task) === false;
			}
		);
	}

	/**
	 * Check if a task progress can be edited
	 *
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {boolean}
	 */
	function canEditProgress(task) {
		return withCache(
			task,
			'can-edit-progress',
			() => {
				return isParentTask(task) === false;
			}
		);
	}

	/**
	 * Returns the current value for the start date (may not be rendered yet) as
	 * a moment instance
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {moment.Moment}
	 */
	function getCurrentStartDate(task) {
		if (task.start instanceof Date) {
			return moment(task.start);
		} else {
			// Date is a string
			return moment(task.start, 'YYYY-MM-DD');
		}
	}

	/**
	 * Returns the current value for the end date (may not be rendered yet) as
	 * a moment instance
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {moment.Moment}
	 */
	function getCurrentEndDate(task) {
		if (task.end instanceof Date) {
			return moment(task.end);
		} else {
			// Date is a string
			return moment(task.end, 'YYYY-MM-DD');
		}
	}

	/**
	 * Returns current task real duration, skipping non work days and including
	 * delays
	 *
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {number}
	 */
	function getTaskActualDuration(task) {
		// Get delays
		const delayedDays = getDelayedDays(task);

		return task.duration + delayedDays;
	}

	/**
	 * @param{import('./ganttWrapper').GanttWrapper.Task} childTask
	 * @param{import('./ganttWrapper').GanttWrapper.Task} parentTask
	 */
	function setTaskParent(childTask, parentTask) {
		// Save previous parent id if any
		const previousParentId = childTask.dependencies.length ?
			childTask.dependencies[0] : undefined;

		// Clear previous dependency
		childTask.dependencies.length = 0;

		// Add parent task
		childTask.dependencies.push(parentTask.id);

		// Check for loops
		try {
			checkForParentLoop(childTask, true);
		} catch (e) {
			alert("Linked parent will cause a loop")
			// Revert operation
			childTask.dependencies.length = 0;

			if (previousParentId) {
				childTask.dependencies.push(previousParentId);
			}

			// Log for debugging
			console.log(e);
			console.log(gantt._dependencyLog);
		}

		invalidateTaskDependants(childTask, true);
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @param {string} newName
	 */
	function setTaskName(task, newName) {
		findTaskById(task.id).name = newName;

		fullRefresh()
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @param {moment.Moment} startDate
	 * @param {boolean} [withFullRefresh]
	 */
	function setTaskStartDate(task, startDate, withFullRefresh = true) {
		task = findTaskById(task.id);

		// Get closest available start date
		startDate = getClosestWorkDay(startDate, 'backwards');

		// We also need to update end date to preserve task duration
		// Find new end date preserving duration
		let endDateAsMoment = getEndDateFromDuration(
			startDate,
			getTaskActualDuration(task),
		);

		// Save new start date
		task.start = formatDateForGanttChart(startDate);
		// Save new shifted end date
		setTaskEndDate(
			task,
			endDateAsMoment,
			false,
		);

		if (withFullRefresh) {
			invalidateTaskDependants(task, true);
			fullRefresh()
		}
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @param {moment.Moment} endDate
	 * @param {boolean} [withFullRefresh]
	 */
	function setTaskEndDate(task, endDate, withFullRefresh = true) {
		task = findTaskById(task.id);

		// Get closest available end date
		endDate = getClosestWorkDay(endDate, 'forwards');

		// End date cant go before start date
		const minEndDate = getCurrentStartDate(task);

		if (endDate.isBefore(minEndDate)) {
			endDate = minEndDate;
		}

		task.end = formatDateForGanttChart(endDate);

		// Update duration
		task.duration =
			getDurationFromDates(
				getCurrentStartDate(task),
				getCurrentEndDate(task)
			)
			- getDelayedDays(task) // Subtract delayed days if any

		if (task.duration < 1) {
			// No matter what happens, a task must last at least a day
			task.duration = 1;
			// Recalculate end date with this min duration taken into account
			setTaskStartDate(
				task,
				getCurrentStartDate(task),
				false
			);
		}

		if (withFullRefresh) {
			invalidateTaskDependants(task, true);
			fullRefresh()
		}
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @param {number} newProgress
	 * @param {boolean} [withFullRefresh]
	 */
	function setTaskProgress(task, newProgress, withFullRefresh = true) {
		// Do some filtering
		if (newProgress === undefined || newProgress === null || isNaN(newProgress)) {
			return;
		}

		if (newProgress > 100) {
			newProgress = 100;
		}
		if (newProgress < 0) {
			newProgress = 0;
		}

		findTaskById(task.id).progress = newProgress;

		if (withFullRefresh) {
			invalidateTaskDependants(task);
			fullRefresh()
		}
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @param {boolean} isMilestone
	 * @param {boolean} [withFullRefresh]
	 */
	function setTaskMilestoneStatus(task, isMilestone, withFullRefresh = true) {
		// Do some filtering
		findTaskById(task.id).is_milestone = isMilestone;

		if (withFullRefresh) {
			invalidateTaskDependants(task);
			fullRefresh()
		}
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @param {number|string} newDuration
	 */
	function setTaskDuration(task, newDuration) {
		// Do some filtering
		if (newDuration === undefined || newDuration === null || isNaN(newDuration)) {
			return;
		}

		// Task must last at least a day
		if (newDuration < 1) {
			newDuration = 1;
		}

		// Save duration
		task.duration = parseInt(newDuration);

		// Trigger start date setter to update end from new duration
		setTaskStartDate(task, getCurrentStartDate(task));
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @param {string} constraintsAsText
	 */
	function setTaskConstraints(task, constraintsAsText) {
		// Build constraints array
		/** @type{GanttWrapper.Constraint[]} */
		let finalConstraintsArray = [];

		let errorParsingConstraints = false;

		// Clean up input
		// Remove trailing spaces
		constraintsAsText = constraintsAsText.trim();
		// Change spaces to commas
		constraintsAsText = constraintsAsText.replace(' ', ',');
		// Remove duplicated commas
		constraintsAsText = constraintsAsText.replace(/,{2,}/, ',');

		if (constraintsAsText.length) {
			// Start parsing text
			const constraints = constraintsAsText.split(',');

			for (const constraint of constraints) {
				if (constraint.length > 0) {
					// Check if constraint is a valid format
					// (\d+) // At least one or more digits for the task ID
					// (SW|SA|EW|EB)? // An optional constraint type
					// ([+\-]\d+)? // An optional offset
					// $
					const testRegex = /^(\d+)(SW|SA|EW|EB)?(([+\-])\d+)?$/i;

					if (testRegex.test(constraint) === false) {
						errorParsingConstraints = true;
						break;
					}

					const constraintParts = constraint.match(testRegex);

					const taskNumber = constraintParts[1];
					const typeAsText = constraintParts[2];
					const offset = parseInt(constraintParts[3]);

					const targetTask = findTaskByIndex(taskNumber);

					if (targetTask === undefined) {
						alert(`Invalid Constraint -- Task #${taskNumber} Not Found`);
						break;
					}

					let type = ''

					switch (typeAsText) {
						case "SW":
							type = 'starts-with'
							break;
						case "EB":
							type = 'ends-before'
							break;
						case "EW":
							type = 'ends-with'
							break;
						case "SA":
						default:
							type = 'starts-after'
							break;
					}

					// noinspection JSValidateTypes
					/** @type {GanttWrapper.Constraint} */
					const constraintObject = {
						target_task_id: targetTask.id,
						type: type,
						days_offset: isNaN(offset) ?
							0 :
							offset,
					}

					finalConstraintsArray.push(constraintObject);
				}
			}
		}


		// Filter duplicated constraints
		const constraintIdMap = {};
		finalConstraintsArray = finalConstraintsArray.filter(constraint => {
			if (constraintIdMap[constraint.target_task_id]) {
				// Skip duplicated constraint
				return false;
			}

			// Mark id as used
			constraintIdMap[constraint.target_task_id] = true;
			return true;
		});

		if (errorParsingConstraints) {
			alert('Invalid Constraint');
		} else {
			task.constraints = finalConstraintsArray;

			invalidateTaskDependants(task, true)
			fullRefresh();
		}
	}


	/**
	 * @param {GanttWrapper.Task} task
	 * @param {boolean} [collapsed]
	 * @param {boolean} [withFullRefresh]
	 */
	function toggleTaskCollapsedStatus(task, collapsed = undefined, withFullRefresh = true) {
		if (isParentTask(task) === false) {
			// We can't collapse a non parent task, so always force it to uncollapsed
			task.collapsed = false

		} else {
			task.collapsed = collapsed === undefined ?
				!task.collapsed : // Toggle current status
				collapsed; // Force status
		}

		if (withFullRefresh) {
			fullRefresh();
		}
	}

	/**
	 * @param {GanttWrapper.Task} task
	 * @param {boolean} [printable]
	 * @param {boolean} [withFullRefresh]
	 */
	function toggleTaskPrintableStatus(task, printable = undefined, withFullRefresh = true) {
		task.printable = printable === undefined ?
			!task.printable : // Toggle current status
			printable; // Force status

		if (withFullRefresh) {
			fullRefresh();
		}
	}

	/**
	 * Check if a task is fully printable, meaning it and its sub children
	 * all are printable
	 *
	 * @param {GanttWrapper.Task} task
	 * @returns {boolean}
	 */
	function isTaskFullyPrintable(task) {
		// No need to check sub tasks if the task itself is not printable
		if (task.printable === false) {
			return false;
		}

		let fullyPrintable = true;

		traverseDescendants(task, subTask => {
			if (subTask.printable === false) {
				fullyPrintable = false
			}
		});

		return fullyPrintable;
	}

	/**
	 * Check if a task is printable but disabled due
	 * to an parent being hidden
	 *
	 * @param {GanttWrapper.Task} task
	 * @returns {boolean}
	 */
	function isTaskNotPrintableDueToParent(task) {
		// No need to check parent tasks if the task itself is not printable
		if (task.printable === false) {
			return true;
		}

		let allParentsArePrintable = true;

		traverseParents(task, parentTask => {
			if (parentTask.printable === false) {
				allParentsArePrintable = false
			}
		});

		return allParentsArePrintable === false;
	}

	/**
	 * Updates task start, end dates & progress based on sub tasks/constraints as required
	 *
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 */
	function calculateTasksDerivedValues(task) {
		// Skip task if already processed
		if (task.needs_dates_calculation === false) {
			return;
		}

		// Check if we are not in a loop
		if (gantt._calculateConstraintsIdHistory.includes(task.id)) {
			throw new RecursiveConstraintError();
		}

		// Add id to history
		gantt._calculateConstraintsIdHistory.push(task.id);


		if (isParentTask(task)) {
			// We need to pull start/end tasks from the sub tasks
			const subTasks = getChildTasks(task);

			// Calculate sub tasks dates first
			subTasks.forEach(_task => {
				calculateTasksDerivedValues(_task);

				if (_task.errors.length > 0) {
					task.errors.push(
						new TaskError('warning', 'subtasks-contains-errors')
					)
				}
			});

			// Get boundary start & end dates
			let earliestStartDate = getCurrentStartDate(subTasks[0]);
			let latestEndDate = getCurrentEndDate(subTasks[0]);

			// Calculate total progress
			let totalProgress = 0;

			subTasks.forEach(subTask => {
				// Get earliest and latest dates from sub tasks
				const startDate = getCurrentStartDate(subTask);
				const endDate = getCurrentEndDate(subTask);

				if (startDate.isBefore(earliestStartDate)) {
					earliestStartDate = startDate;
				}
				if (endDate.isAfter(latestEndDate)) {
					latestEndDate = endDate;
				}

				// Add to total progress
				totalProgress += subTask.progress;
			})

			// Update task dates
			setTaskStartDate(task, earliestStartDate, false);
			setTaskEndDate(task, latestEndDate, false);

			// Update total progress
			const tasksProgress = Math.floor(totalProgress / subTasks.length);
			setTaskProgress(task, tasksProgress, false);
		} else {
			// Clean up constraints first
			task.constraints = task.constraints.filter(constraint => {
				// Check if the target task has not been deleted
				// noinspection UnnecessaryLocalVariableJS
				const targetTaskExists = !!findTaskById(constraint.target_task_id);
				return targetTaskExists;
			})

			// Child tasks, check for constraints
			if (task.constraints.length > 0) {
				/**
				 * @type {moment.Moment[]}
				 */
				const earliestStartDates = [];
				const latestStartDates = [];
				const earliestEndDates = [];
				const latestEndDates = [];

				for (const constraint of task.constraints) {
					const targetTask = findTaskById(constraint.target_task_id);

					// Make sure all constraints task are updated
					try {
						calculateTasksDerivedValues(targetTask);

						if (targetTask.errors.length > 0) {
							task.errors.push(
								new TaskError('warning', 'constraint-task-contains-errors')
							)

							// End calculations early
							return;
						}
					} catch (e) {
						if (e instanceof RecursiveConstraintError) {
							task.errors.push(
								new TaskError('error', 'recursive-constraints')
							);

							// End calculations early
							return;
						} else {
							// We can't handle this error
							throw e;
						}
					}

					switch (constraint.type) {
						case "starts-with":
							earliestStartDates.push(
								getClosestWorkDay(
									getCurrentStartDate(targetTask)
										.clone()
										.add(constraint.days_offset, 'days'),
									constraint.days_offset >= 0 ? 'forwards' : 'backwards',
								)
							);
							break;
						case "starts-after":
							latestStartDates.push(
								getClosestWorkDay(
									getCurrentEndDate(targetTask)
										.clone()
										.add(constraint.days_offset, 'days')
										.add(1, 'day'), // We add a day to compensate for how frappe gantt store the end dates
									constraint.days_offset >= 0 ? 'forwards' : 'backwards',
								)
							);
							break;
						case "ends-before":
							earliestEndDates.push(
								getClosestWorkDay(
									getCurrentStartDate(targetTask)
										.clone()
										.add(constraint.days_offset, 'days')
										.add(-1, 'day'),// We subtract a day to compensate for how frappe gantt store the end dates
									constraint.days_offset > 0 ? 'forwards' : 'backwards', // Defaults backwards
								)
							)
							break;
						case "ends-with":
							latestEndDates.push(
								getClosestWorkDay(
									getCurrentEndDate(targetTask)
										.clone()
										.add(constraint.days_offset, 'days'),
									constraint.days_offset >= 0 ? 'forwards' : 'backwards', // Defaults backwards
								)
							)
							break;

					}
				}

				const earliestStartDate = earliestStartDates.length > 0 ?
					moment.min(earliestStartDates) : undefined;
				const latestStartDate = latestStartDates.length > 0 ?
					moment.max(latestStartDates) : undefined;

				const earliestEndDate = earliestEndDates.length > 0 ?
					moment.min(earliestEndDates) : undefined;
				const latestEndDate = latestEndDates.length > 0 ?
					moment.max(latestEndDates) : undefined;

				/**
				 * @var {moment.Moment}
				 */
				let targetStartDate;
				/**
				 * @var {moment.Moment}
				 */
				let targetEndDate;

				if (earliestStartDate && latestStartDate) {
					// We have both a min and max for start date
					if (earliestStartDate.isBefore(latestStartDate)) {
						task.errors.push(
							new TaskError('error', 'constraints-cant-be-solved')
						);
					}

					targetStartDate = moment.min([earliestStartDate, latestStartDate]);
				} else {
					// Pick whichever date we have
					targetStartDate = earliestStartDate ? earliestStartDate : latestStartDate;
				}

				if (earliestEndDate && latestEndDate) {
					// We have both a min and max for end date
					if (earliestEndDate.isBefore(latestEndDate)) {
						task.errors.push(
							new TaskError('error', 'constraints-cant-be-solved')
						);
					}

					targetEndDate = moment.min([earliestEndDate, latestEndDate]);
				} else {
					// Pick whichever date we have
					targetEndDate = earliestEndDate ? earliestEndDate : latestEndDate;
				}

				if (targetStartDate && targetEndDate) {
					if (targetStartDate.isAfter(targetEndDate)) {
						task.errors.push(
							new TaskError('error', 'constraints-cant-be-solved')
						);

						// Force end date to day after start date
						targetEndDate = targetStartDate.clone();
					}
				}

				if (targetStartDate) {
					// Update start date
					setTaskStartDate(task, targetStartDate, false);
				}

				if (targetEndDate) {
					const currentStartDate = getCurrentStartDate(task);

					if (targetEndDate.isBefore(currentStartDate)) {
						// Shift start date and preserve duration
						const duration = getTaskActualDuration(task);

						const newStartDate = targetEndDate.clone()
							.add((-duration) + 1, 'days');

						setTaskStartDate(task, newStartDate, false);
						setTaskEndDate(task, targetEndDate, false);
					} else {
						setTaskEndDate(task, targetEndDate, false);
					}
				}
			}
		}

		// Clear flag
		task.needs_dates_calculation = false;

		// Remove task from history
		gantt._calculateConstraintsIdHistory.pop()
	}

	/**
	 * Marks all tasks whose date values are dependant of this task to be recalculated on next Full Refresh
	 *
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @param {boolean} invalidateSelf Also require recalculation on the given task
	 */
	function invalidateTaskDependants(task, invalidateSelf = false) {
		// Check if we already invalidated this task
		if (gantt._invalidateDependantsIdHistory.includes(task.id)) {
			return;
		}

		// Add task id to history
		gantt._invalidateDependantsIdHistory.push(task.id);

		// Invalidate parents
		const parents = getTaskParents(task);

		if (parents.length > 0) {
			const firstParent = parents[0];
			invalidateTaskDependants(firstParent, true);
		}

		// Invalidate tasks with constraints pointing to this task
		const dependants = getConstraintsDependants(task);

		dependants.forEach(dependants => {
			invalidateTaskDependants(dependants, true);
		})

		if (invalidateSelf) {
			task.needs_dates_calculation = true;
		}

		// Remove task from history
		gantt._invalidateDependantsIdHistory.pop()
	}

	/**
	 * Get all tasks whose constraints point to the given task
	 *
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {import('./ganttWrapper').GanttWrapper.Task[]}
	 */
	function getConstraintsDependants(task) {
		return withCache(
			task,
			'constraints-dependants',
			() => {
				const dependants = [];
				gantt._getTasks().forEach(_task => {
					// Check each tasks constraints
					for (const constraint of _task.constraints) {
						if (constraint.target_task_id === task.id) {
							// References the given task, store it and stop
							// checking for constraints
							dependants.push(_task);
							break;
						}
					}
				});

				return dependants;
			}
		);
	}

	function moveSelectedTasksUp() {
		waitUntilRefreshFinished(() => {
			const tasks = getSelectedTasks();

			// We first group tasks in consecutive groups
			// So we can easily stop moving each group if
			// one of the preceding tasks can't move further
			/**
			 * @type {GanttWrapper.Task[][]}
			 */
			const consecutiveTaskGroups = [];

			const lastGroup = tasks.reduce((currentGroup, task) => {
				if (currentGroup.length === 0) {
					// First task in group
					currentGroup.push(task);
				} else {
					// Check if selected task is the next sibling of previous task
					const previousTask = currentGroup[currentGroup.length - 1];

					const precedingSiblings = getPrecedingSiblingTasks(task).reverse();

					if (precedingSiblings[0] && precedingSiblings[0].id === previousTask.id) {
						// Consecutive task keep in the same group
						currentGroup.push(task);
					} else {
						// Separate group, store current group and reset group
						consecutiveTaskGroups.push(currentGroup);
						currentGroup = [task];
					}
				}

				return currentGroup;
			}, []);

			consecutiveTaskGroups.push(lastGroup);

			consecutiveTaskGroups.forEach(taskGroup => {
				for (const task of taskGroup) {
					if (moveTaskUp(task, false) === false) {
						// If a task couldn't be moved upwards, stop moving tasks below it
						break;
					}
				}
			})

			if (tasks.length > 0) {
				fullRefresh();
			}
		})
	}

	function expandTasks() {
		waitUntilRefreshFinished(() => {
			let tasks = getSelectedTasks();

			if (tasks.length === 0) {
				tasks = getTasks();
			}

			tasks.forEach(task => {
				toggleTaskCollapsedStatus(task, false, false);
			})

			fullRefresh();
		})
	}

	function collapseTasks() {
		waitUntilRefreshFinished(() => {
			let tasks = getSelectedTasks();

			if (tasks.length === 0) {
				tasks = getTasks();

				// Clear task selection restore cache when hiding all tasks
				gantt._gridSelectedTasks.length = 0;
			}

			tasks.forEach(task => {
				toggleTaskCollapsedStatus(task, true, false);
			})


			fullRefresh();
		})
	}

	function enablePrintableFlagForTasks() {
		waitUntilRefreshFinished(() => {
			let tasks = getSelectedTasks();

			if (tasks.length === 0) {
				tasks = getTasks();

				// Clear task selection
				gantt._gridSelectedTasks.length = 0;
			}

			tasks.forEach(task => {
				toggleTaskPrintableStatus(task, true, false);
			})

			fullRefresh();
		})
	}

	function disablePrintableFlagForTasks() {
		waitUntilRefreshFinished(() => {
			let tasks = getSelectedTasks();

			if (tasks.length === 0) {
				tasks = getTasks();

				// Clear task selection
				gantt._gridSelectedTasks.length = 0;
			}

			tasks.forEach(task => {
				toggleTaskPrintableStatus(task, false, false);
			})

			fullRefresh();
		})
	}

	/**
	 * @param {GanttWrapper.Task} task
	 * @param {boolean} [withFullRefresh]
	 * @returns {boolean} returns true if the task actually changed positions false otherwise
	 */
	function moveTaskUp(task, withFullRefresh = false) {
		const taskIndex = getTaskIndex(task);
		if (taskIndex <= 1) {
			// We can't go above the title task
			return false;
		}

		const precedingSiblings = getPrecedingSiblingTasks(task);

		if (precedingSiblings.length === 0) {
			return false;
		}

		// Get index of last sibling
		const lastSibling = precedingSiblings[precedingSiblings.length - 1];

		const lastSiblingIndex = getTaskIndex(lastSibling);

		// Swap places
		gantt._all_tasks[lastSiblingIndex] = task;
		gantt._all_tasks[taskIndex] = lastSibling;

		if (withFullRefresh) {
			fullRefresh();
		}

		return true;
	}

	function moveSelectedTasksDown() {
		waitUntilRefreshFinished(() => {
			const tasks = getSelectedTasks()

			// We first group tasks in consecutive groups
			// So we can easily stop moving each group if
			// one of the following tasks can't move further
			/**
			 * @type {GanttWrapper.Task[][]}
			 */
			const consecutiveTaskGroups = [];

			const lastGroup = tasks.reverse().reduce((currentGroup, task) => {
				if (currentGroup.length === 0) {
					// First task in group
					currentGroup.push(task);
				} else {
					// Check if selected task is the next sibling of previous task
					const nextTask = currentGroup[currentGroup.length - 1];

					const followingSiblings = getFollowingSiblingTasks(task);

					if (followingSiblings[0] && followingSiblings[0].id === nextTask.id) {
						// Consecutive task keep in the same group
						currentGroup.push(task);
					} else {
						// Separate group, store current group and reset group
						consecutiveTaskGroups.push(currentGroup);
						currentGroup = [task];
					}
				}

				return currentGroup;
			}, []);

			consecutiveTaskGroups.push(lastGroup);

			consecutiveTaskGroups.forEach(taskGroup => {
				for (const task of taskGroup) {
					if (moveTaskDown(task, false) === false) {
						// If a task couldn't be moved downwards, stop moving tasks below it
						break;
					}
				}
			})

			if (tasks.length > 0) {
				fullRefresh();
			}
		})
	}

	/**
	 * @param {GanttWrapper.Task} task
	 * @param {boolean} [withFullRefresh]
	 * @returns {boolean} returns true if the task actually changed positions false otherwise
	 */
	function moveTaskDown(task, withFullRefresh = false) {
		const taskIndex = getTaskIndex(task);
		if (taskIndex >= gantt._all_tasks.length - 1) {
			// We are already the last task
			return false;
		}

		const followingSiblings = getFollowingSiblingTasks(task);

		if (followingSiblings.length === 0) {
			return false;
		}

		// Get index of following sibling
		const followingSibling = followingSiblings[0];

		const followingSiblingIndex = getTaskIndex(followingSibling);

		// Swap places
		gantt._all_tasks[followingSiblingIndex] = task;
		gantt._all_tasks[taskIndex] = followingSibling;

		if (withFullRefresh) {
			fullRefresh();
		}

		return true;
	}

	function indentSelectedTasks() {
		waitUntilRefreshFinished(() => {
			const tasks = getSelectedTasks();

			tasks.forEach(task => indentTask(task, false))

			if (tasks.length > 0) {
				fullRefresh();
			}
		})
	}

	/**
	 * @param {GanttWrapper.Task} task
	 * @param {boolean} [withFullRefresh]
	 */
	function indentTask(task, withFullRefresh = true) {
		const precedingSiblings = getPrecedingSiblingTasks(task);

		// Can only indent if we have a sibling above
		if (precedingSiblings.length) {
			const lastPrecedingSibling = precedingSiblings[precedingSiblings.length - 1];

			// Invalidate both before and after;
			invalidateTaskDependants(task, true);

			// Make last preceding sibling parent of the given task
			setTaskParent(task, lastPrecedingSibling);

			invalidateTaskDependants(task, true);

			if (withFullRefresh) {
				fullRefresh()
			}
		}
	}

	function outdentSelectedTasks() {
		waitUntilRefreshFinished(() => {
			const tasks = getSelectedTasks();

			tasks.forEach(task => outdentTask(task, false))

			if (tasks.length > 0) {
				fullRefresh();
			}
		})
	}

	/**
	 * @param {GanttWrapper.Task} task
	 * @param {boolean} [withFullRefresh]
	 */
	function outdentTask(task, withFullRefresh = true) {
		// Can only outdent if task has a parent
		if (task.dependencies.length > 0) {
			// Invalidate both before and after;
			invalidateTaskDependants(task, true);

			const parents = getTaskParents(task);
			const followingSiblings = getFollowingSiblingTasks(task);

			if (parents.length === 1) {
				// Only one parent, make root task
				task.dependencies.length = 0;
			} else {
				// Move one level
				const newParent = parents[1];
				setTaskParent(task, newParent);
			}


			// Also make all siblings tasks following the given task
			// children of it
			followingSiblings.forEach(siblingTask => {
				setTaskParent(siblingTask, task);
			})

			invalidateTaskDependants(task, true);

			if (withFullRefresh) {
				fullRefresh()
			}
		}
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {import('./ganttWrapper').GanttWrapper.Task[]}
	 */
	function getPrecedingSiblingTasks(task) {
		/** @type {import('./ganttWrapper').GanttWrapper.Task[]} */
		let siblings;

		let precedingSiblings = [];

		if (task.dependencies.length === 0) {
			// Siblings are root tasks
			siblings = getRootTasks();
		} else {
			// Get parent task
			const parentTask = findTaskById(task.dependencies[0]);

			// Get siblings
			siblings = getChildTasks(parentTask);
		}

		// Save all siblings up to the given task
		for (const sibling of siblings) {
			if (sibling.id === task.id) {
				break;
			}

			precedingSiblings.push(sibling);
		}

		return precedingSiblings;
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {import('./ganttWrapper').GanttWrapper.Task[]}
	 */
	function getFollowingSiblingTasks(task) {
		/** @type {import('./ganttWrapper').GanttWrapper.Task[]} */
		let siblings;

		let followingSiblings = [];

		if (task.dependencies.length === 0) {
			// Siblings are root tasks
			siblings = getRootTasks();
		} else {
			// Get parent task
			const parentTask = findTaskById(task.dependencies[0]);

			// Get siblings
			siblings = getChildTasks(parentTask);
		}

		let isAfterSibling = false;
		// Save all siblings after the given task
		for (const sibling of siblings) {
			if (sibling.id === task.id) {
				isAfterSibling = true;
				continue;
			}

			if (isAfterSibling) {
				followingSiblings.push(sibling);
			}
		}

		return followingSiblings;
	}

	/**
	 * @return {import('./ganttWrapper').GanttWrapper.Task[]}
	 */
	function getRootTasks() {
		return gantt
			._all_tasks
			.slice(1) // Skip initial hidden task
			.filter(task => task.dependencies.length === 0);
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {import('./ganttWrapper').GanttWrapper.Task[]}
	 */
	function getChildTasks(task) {
		const childTasks = [];

		gantt._all_tasks.forEach(_task => {
			if (_task.dependencies.includes(task.id)) {
				childTasks.push(_task);
			}
		})

		return childTasks;
	}

	// noinspection JSUnusedLocalSymbols -- Unused for now, but may be useful later
	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task[]} ignoredTasks
	 * @param {string} selectionText
	 * @param {(selectedTask: import('./ganttWrapper').GanttWrapper.Task) => void} selectionHandler
	 */
	function startTaskSelectionProcess(ignoredTasks, selectionText, selectionHandler) {
		// Add modifier class to container
		gantt._container.classList.add('selecting-link');

		// Hide popup so we can select easily
		hideTaskPopup();

		// Add selection boxes to all tasks
		gantt._all_tasks.forEach((task, index) => {
			if (
				index === 0
			) {
				// Skip title or ignored task
				return;
			}

			// noinspection DuplicatedCode
			if (ignoredTasks.includes(task)) {
				// Add blocker so we can't interact with the bar
				// Build selection box
				const selectionBox = createElement(`
					<div class="selection-blocker" data-task-id="${task.id}">
					</div>
				`)

				// Get task bar coordinates
				const taskBarElement = gantt._ganttContainer.querySelector(`.bar-wrapper[data-id="${task.id}"]`)

				const barCoordinates = getRelativePositionFromContainer(taskBarElement);

				// Place selection box over task bar
				selectionBox.style.top = `${barCoordinates.top}px`;
				selectionBox.style.left = `${barCoordinates.left}px`;
				selectionBox.style.height = `${barCoordinates.height}px`;
				selectionBox.style.width = `${barCoordinates.width}px`;

				// Add to container
				gantt._ganttContainer.appendChild(selectionBox);
			} else {
				// Build selection box
				const selectionBox = createElement(`
					<div class="selection-box" data-task-id="${task.id}">
						<div class="hover-message">
							${selectionText}
						</div>
					</div>
				`)

				// Get task bar coordinates
				const taskBarElement = gantt._ganttContainer.querySelector(`.bar-wrapper[data-id="${task.id}"]`)

				const barCoordinates = getRelativePositionFromContainer(taskBarElement);

				// Place selection box over task bar
				selectionBox.style.top = `${barCoordinates.top}px`;
				selectionBox.style.left = `${barCoordinates.left}px`;
				selectionBox.style.height = `${barCoordinates.height}px`;
				selectionBox.style.width = `${barCoordinates.width}px`;

				// Handle selection
				selectionBox.addEventListener('click', () => {
					endTaskSelectionProcess();
					selectionHandler(task);
				})

				// Add to container
				gantt._ganttContainer.appendChild(selectionBox);
			}
		})

		/** Disable certain actions during selection */
		// No adding tasks
		gantt._container.querySelectorAll('.add-task-button').forEach(addTaskButton => {
			addTaskButton.disabled = true;
			addTaskButton.classList.add('cursor-not-allowed')

			const originalTitle = addTaskButton.title;

			addTaskButton.title = "Can't add task during selection";

			// Wait until selection stops
			waitUntil(() => isSelectionProcessActive() === false, {msBetweenAttempts: 16})
				.then(() => {
					// Revert changes
					addTaskButton.disabled = false;
					addTaskButton.classList.remove('cursor-not-allowed')

					addTaskButton.title = originalTitle;
				})
		})
	}

	function endTaskSelectionProcess() {
		gantt._container.classList.remove('selecting-link');

		// Remove all blockers and selection boxes
		gantt._container.querySelectorAll('.selection-blocker,.selection-box')
			.forEach(box => {
				box.parentElement.removeChild(box);
			})
	}

	/**
	 * @return boolean
	 */
	function isSelectionProcessActive() {
		return gantt._container.classList.contains('selecting-link');
	}

	/**
	 * Check if there are any cycles on the dependencies tree following the parents
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @param {boolean} [firstCall]
	 */
	function checkForParentLoop(task, firstCall = false) {
		if (firstCall) {
			// Clear _dependencyLog
			gantt._dependencyLog = [];
		}

		if (gantt._dependencyLog.includes(task.id)) {
			// Cycle detected

			// Add itself to dependency log so we can output the log
			gantt._dependencyLog.push(task.id);

			throw 'Parent Cycle Detected';
		}

		// Add itself to dependency log
		gantt._dependencyLog.push(task.id);

		task.dependencies
			.map(taskId => findTaskById(taskId))
			.forEach(parentTask => checkForParentLoop(parentTask));

		// Pop itself from dependency log
		gantt._dependencyLog.pop();
	}

	/**
	 * @param {SVGGeometryElement} path The path where the remove relationship icon will be shown
	 * @param {MouseEvent} mouseEvent
	 */
	function showRemoveRelationshipIconOnPath(path, mouseEvent) {
		if (gantt.bar_being_dragged) {
			// Do nothing if a bar is being dragged
			return;
		}
		path.classList.add('hovering');

		const pathLength = path.getTotalLength();

		// Convert mouse event coordinates to relative to parent container
		const relativeCoordinates = getRelativePositionFromGanttContainer(
			new DOMRect(
				mouseEvent.clientX,
				mouseEvent.clientY,
			),
		);

		// Find closes point on path
		const resolution = 100;

		let delta = pathLength / resolution;

		// We want to skip the start and end of the line
		const pathStart = 10; // Skip connection with origin bar
		const pathEnd = pathLength - 30; // Skip arrow

		let closestPoint = undefined;
		let closestDistance = Number.MAX_SAFE_INTEGER;
		for (let pathPosition = pathStart; pathPosition < pathEnd; pathPosition += delta) {
			const pointOnPath = path.getPointAtLength(pathPosition);
			const x = relativeCoordinates.x - pointOnPath.x;
			const y = relativeCoordinates.y - pointOnPath.y;

			const distanceFromClick = Math.sqrt((x * x) + (y * y));
			if (distanceFromClick < closestDistance) {
				closestDistance = distanceFromClick;
				closestPoint = pointOnPath;
			}
		}

		// Move icon to closest point
		// noinspection JSUnresolvedVariable
		gantt._removeRelationshipIcon.style.top = `${closestPoint.y}px`;
		// noinspection JSUnresolvedVariable
		gantt._removeRelationshipIcon.style.left = `${closestPoint.x}px`;

		gantt._removeRelationshipIcon.classList.add('show');

		// Because fast movements may trigger this but not the mouseleave handler
		// We want to check a after a little while if cursor is still over the task
		setTimeout(() => {
			const hoveredElements = document.querySelectorAll(':hover');

			// Retrieve related fake path
			const fakePath = path.parentElement
				.querySelector(`.hover-only-path[data-path-id="${path.dataset.pathId}"]`);

			let stillHovering = false;
			for (const hoveredElement of hoveredElements) {
				if (hoveredElement.isSameNode(fakePath)) {
					stillHovering = true;
					break;
				}
			}
			if (!stillHovering) {
				// We are no longer hovering over the path, hide it
				hideRemoveRelationshipIconOnPath(path);
			}
		}, 32);
	}

	/**
	 * @param {SVGGeometryElement} path The path where the remove relationship icon will be hidden
	 */
	function hideRemoveRelationshipIconOnPath(path) {
		path.classList.remove('hovering');
		gantt._removeRelationshipIcon.classList.remove('show');
	}

	/**
	 * @param {SVGGeometryElement} path The path that contains the relationship to remove
	 */
	function removePathRelationship(path) {
		const childTask = findTaskById(path.dataset.to);
		const parentTaskId = path.dataset.from;

		childTask.dependencies = childTask.dependencies
			.filter(taskId => taskId !== parentTaskId);

		hideRemoveRelationshipIconOnPath(path)
		fullRefresh()
	}

	/**
	 * Used to enable or disable dragging
	 * @param {PointerEvent} event
	 */
	function handleContainerMouseDown(event) {
		if (
			gantt._svgContainer.isSameNode(event.target) || // If clicked on the background
			event.target.classList.contains('grid-row') || // Or the rows containing the tasks...
			event.target.classList.contains('row-line') || // Or one of the dividers...
			event.target.nodeName === 'path' || // Or one of the path dividers
			event.target.classList.contains('grid-background') || // Or the background...
			event.target.classList.contains('grid-header') || // Or the header...
			event.target.classList.contains('lower-text') || // Or the header text...
			event.target.classList.contains('title-bar-screen') || // Or the title bar...
			event.target.classList.contains('non-workable-day-timeline-highlight') || // Or when clicking on a timeline highlight...
			event.target.classList.contains('today-highlight') || // Or current day highlight...
			event.target.classList.contains('gantt-chart-drag-padding-container') // Or extra padding made to be dragged...
		) {
			gantt._ganttContainer.classList.add('dragging');
			gantt._draggingBackground = true;
		}

		// We clicked on the resize anchor and we are on the split view
		if (event.target.classList.contains('task-panel-width-anchor') && gantt._editorViewType === 'split') {
			gantt._container.classList.add('resizing-panel-width');
			gantt._changingTaskPanelWidth = true;
		}
	}

	/**
	 * Used to enable or disable dragging
	 */
	function handleContainerMouseUp() {
		// Stop dragging gantt chart
		gantt._draggingBackground = false;
		gantt._ganttContainer.classList.remove('dragging');

		// Stop dragging task panel width
		gantt._container.classList.remove('resizing-panel-width');
		gantt._changingTaskPanelWidth = false;
	}

	/**
	 * Bound to the container/viewport
	 * @param {PointerEvent} e
	 */
	function handleMouseMoveOverViewport(e) {
		// Get position relative to scrollable container
		const relativeBox = getRelativePositionFromGanttContainer(
			new DOMRect(
				e.clientX,
				e.clientY,
			)
		);

		// Get nearest anchor
		const nearestAnchor = getNearestAnchor(relativeBox.x);

		updateInlineAddTaskButton(e, relativeBox, nearestAnchor);
		updateVerticalAnchorGuide(e, relativeBox, nearestAnchor);
		handleDrag(e);
	}

	/**
	 * @param {PointerEvent} e
	 * @param {DOMRect} relativeBox
	 * @param {import('./ganttWrapper').GanttWrapper.DateAnchor} nearestAnchor
	 */
	function updateInlineAddTaskButton(e, relativeBox, nearestAnchor) {
		const button = gantt._inlineAddTaskButton;

		const mouseOverToolsContainer = gantt._bottomToolsContainer.contains(e.target);

		if (mouseOverToolsContainer && !gantt.bar_being_dragged) {
			// Show button and position horizontally

			// noinspection JSUnresolvedVariable
			button.style.left = `${nearestAnchor.xPosition}px`;

			const startDate = getClosestWorkDay(nearestAnchor.date, 'forwards');
			const endDate = getEndDateFromDuration(startDate, DEFAULT_TASK_DURATION);

			button.dataset.start = formatDateForGanttChart(startDate);
			button.dataset.end = formatDateForGanttChart(endDate);

			button.classList.add('show');
		} else {
			button.classList.remove('show');
		}
	}

	/**
	 * @param {PointerEvent} e
	 * @param {DOMRect} relativeBox
	 * @param {import('./ganttWrapper').GanttWrapper.DateAnchor} nearestAnchor
	 */
	function updateVerticalAnchorGuide(e, relativeBox, nearestAnchor) {
		const guide = gantt._verticalAnchorGuide;

		// noinspection JSUnresolvedVariable
		const showBar =
			relativeBox.y > 0 && // Cursor is below the start of the chart viewport...
			relativeBox.y < gantt._bottomToolsContainer.dataset.bottom;// ... and above the end of the tools container ...

		if (showBar) {
			guide.classList.remove('hidden');

			// noinspection JSUnresolvedVariable
			guide.style.left = `${nearestAnchor.xPosition}px`;
			// noinspection JSUnresolvedVariable
			guide.style.height = gantt._bottomToolsContainer.style.top;

			guide.innerHTML = `<div class="date-label">${nearestAnchor.date.format('MM/DD/YYYY')}</div>`;
		} else {
			guide.classList.add('hidden');
		}
	}

	function getTasksPanelMaxWidth() {
		const taskPanelEntriesContainer = gantt._taskPanelContainer.querySelector('.ag-center-cols-viewport');

		const maxWidthFromContainer = gantt._viewport.getBoundingClientRect().width - 120; // Margin from container
		const maxWidthFromContents = taskPanelEntriesContainer.children.length > 0 ? // Only check for scroll width if it has contents
			taskPanelEntriesContainer.scrollWidth : Number.MAX_SAFE_INTEGER;

		return Math.min(maxWidthFromContainer, maxWidthFromContents);
	}

	function enforceTaskPanelWidthLimits() {
		// Set limits
		if (gantt._taskPanelWidth < TASK_PANEL_MIN_WIDTH) {
			gantt._taskPanelWidth = TASK_PANEL_MIN_WIDTH;
		}

		const maxWidth = getTasksPanelMaxWidth();

		if (gantt._taskPanelWidth > maxWidth) {
			gantt._taskPanelWidth = maxWidth;
		}
	}

	/**
	 * @param {PointerEvent} e
	 */
	function handleDrag(e) {
		const isMouseMovement = e.pointerType === 'mouse';
		if (
			gantt._draggingBackground ||
			gantt._changingTaskPanelWidth
		) {
			if (isMouseMovement) {
				// We check periodically if we are outside the main
				// container, so we can turn off dragging, but only
				// for mouse events
				setTimeout(() => {
					const hoveredElements = document.querySelectorAll(':hover');

					let stillHovering = false;
					for (const hoveredElement of hoveredElements) {
						if (hoveredElement.isSameNode(gantt._container)) {
							stillHovering = true;
							break;
						}
					}

					if (!stillHovering) {
						handleContainerMouseUp()
					}
				}, 32);
			}
		}

		if (gantt._draggingBackground) {
			const deltaX = e.movementX;
			const deltaY = e.movementY;

			if (isMouseMovement) {
				gantt._ganttContainer.scrollLeft += -(deltaX * 2);
				gantt._viewport.scrollTop += -(deltaY * 2);
			} else {
				gantt._ganttContainer.scrollLeft += (deltaX * 2);
				gantt._viewport.scrollTop += (deltaY * 2);
			}
		}

		if (gantt._changingTaskPanelWidth) {
			const deltaX = e.movementX;

			gantt._taskPanelWidth += deltaX;

			enforceTaskPanelWidthLimits();

			// Rerender gantt
			gantt.render();

			// Re render fake columns
			setupTaskPanelFakeHeaders();

			// TODO: Update task panel width in case it should shrink
			// due to the with the new columns widths
		}
	}


	/**
	 * Gets the nearest anchor from a X coordinate relative to
	 * the scrollable container
	 *
	 * @param xPosition
	 * @return {import('./ganttWrapper').GanttWrapper.DateAnchor}
	 */
	function getNearestAnchor(xPosition) {
		// Binary search by X coordinate
		let start = 0;
		const positions = Object.keys(gantt._dateAnchorsCacheByPosition);
		let end = positions.length - 1;
		while (start <= end) {
			let mid = Math.floor((start + end) / 2);

			const midValue = positions[mid];
			const nextValue = positions[mid + 1] ? positions[mid + 1] : Number.MAX_SAFE_INTEGER;
			if (midValue < xPosition && xPosition < nextValue) {
				return gantt._dateAnchorsCacheByPosition[midValue];
			}

			if (xPosition < midValue) {
				end = mid - 1;
			} else {
				start = mid + 1;
			}
		}

		// Not found, return last position
		return gantt._dateAnchorsCacheByPosition[positions[positions.length - 1]];
	}

	/**
	 * Returns the location of the last bar container
	 * Maybe cache this
	 *
	 * @return {DOMRect}
	 */
	function getLastBarLocation() {
		const lastBar = gantt._ganttContainer.querySelector('.grid-row:last-of-type');
		return getRelativePositionFromContainer(lastBar);
	}

	// noinspection JSUnusedLocalSymbols
	/**
	 * Returns the the bar for the given task
	 *
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {Element}
	 */
	function getTaskRelatedBarElement(task) {
		return gantt._ganttContainer.querySelector(`.bar-wrapper[data-id="${task.id}"]`)
	}

	/**
	 * Returns the gantt chart grid row for the given task
	 *
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {HTMLElement}
	 */
	function getTaskRelatedGanttRowElement(task) {
		const taskRealIndex = gantt._all_tasks.findIndex(_task => _task.id === task.id);

		return gantt._ganttContainer.querySelectorAll('.grid-row')[taskRealIndex];
	}


	// noinspection JSUnusedLocalSymbols
	/**
	 * Returns the location of the bar for the given task
	 *
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {DOMRect}
	 */
	function getTaskRelatedBarLocation(task) {
		const taskRealIndex = gantt._all_tasks.findIndex(_task => _task.id === task.id);

		const bar = gantt._ganttContainer.querySelectorAll('.grid-row')[taskRealIndex];
		return getRelativePositionFromContainer(bar);
	}

	/**
	 * Returns the location of the first bar container
	 * Maybe cache this
	 * @return {DOMRect}
	 */
	function getFirstBarLocation() {
		return withCache(null, 'first-bar-location', () => {
			const firstBar = gantt._ganttContainer.querySelector('.grid-row:first-of-type');
			return getRelativePositionFromContainer(firstBar);
		})
	}

	/**
	 * @return {GanttWrapper.Task[]}
	 */
	function getTasks() {
		// Skip the first task
		return gantt._all_tasks.slice(1);
	}

	/**
	 * @return {GanttWrapper.Task[]}
	 */
	function getVisibleTasks() {
		return getTasks().filter(task => task.hidden === false);
	}

	/**
	 * @param {import('./ganttWrapper').GanttWrapper.Task} task
	 * @return {number}
	 */
	function getTaskDisplayIndex(task) {
		const displayedTasks = getTasks();

		for (const _task of displayedTasks) {
			const index = displayedTasks.indexOf(_task);
			if (_task.id === task.id) {
				return index + 1;
			}
		}

		return 0;
	}

	/**
	 * Returns the initial auto increment value for the tasks ids
	 * from the existing tasks
	 */
	function getInitialAutoIncrementValue() {
		// Fake initial tasks id
		let highestExistingId = -1;

		gantt._all_tasks.forEach(task => {
			const taskId = parseInt(task.id);
			if (taskId > highestExistingId) {
				highestExistingId = taskId;
			}
		})

		let initialAutoIncrementValue = highestExistingId + 1;

		// We want to start ids at 1
		if (initialAutoIncrementValue <= 0) {
			initialAutoIncrementValue = 1;
		}

		return initialAutoIncrementValue;
	}

	/**
	 * Caches the result of the given callback for future uses, cleared
	 * between refreshes
	 *
	 * @param {import('./ganttWrapper').GanttWrapper.Task|null} task
	 * @param {string} cacheKey
	 * @param {function} callback
	 * @param {boolean} [skipCache]
	 * @return {any}
	 */
	function withCache(task, cacheKey, callback, skipCache = false) {
		if (task) {
			// Append task id to key
			cacheKey = task.id + '-' + cacheKey;
		}

		// Return cached value if it exists...
		if (gantt._cachedValues[cacheKey] && skipCache === false) {
			return gantt._cachedValues[cacheKey];
		}

		// ...execute callback and save value if not
		const callbackResult = callback();

		gantt._cachedValues[cacheKey] = callbackResult;

		return callbackResult;
	}

	function clearCache() {
		gantt._cachedValues = {};
	}

	/**
	 * We need to prevent movement on the gantt chart of the tasks
	 * with constraints
	 */
	function setupConstraintInteractionLocks() {
		// Clear previous blockers
		gantt._ganttContainer.querySelectorAll('.task-interaction-blocker').forEach(blocker => {
			blocker.parentElement.removeChild(blocker);
		})

		getVisibleTasks().forEach(task => {
			if (
				shouldBlockLeftHandle(task) ||
				shouldBlockRightHandle(task) ||
				shouldBlockMainBody(task)
			) {
				const taskBarElement = gantt._ganttContainer.querySelector(`.bar-wrapper[data-id="${task.id}"]`)

				const barCoordinates = getRelativePositionFromContainer(taskBarElement.querySelector('.bar'));
				taskBarElement.querySelector('.bar-label').classList.add('pointer-events-none');

				// Get task bar coordinates
				// Get right handle width
				const rightHandle = taskBarElement.querySelector('.handle.right');
				const rightHandleWidth = parseInt(rightHandle.getAttribute('width'));

				// Get left handle width
				const leftHandle = taskBarElement.querySelector('.handle.left');
				const leftHandleWidth = parseInt(leftHandle.getAttribute('width'));

				// Container to group all blockers
				let blockerWrapper = createElement('<div class="blocker-wrapper"></div>')

				let blockerBox;
				let leftBlockerBox;
				let rightBlockerBox;

				const blockerTitle = gantt._wrapperOptions.viewOnly ?
					'' :
					'Derived from sub tasks';

				if (shouldBlockMainBody(task)) {
					blockerBox = createElement(`
						<div
							class="task-interaction-blocker main-body-blocker" data-task-id="${task.id}"
							title="${blockerTitle}"
						>
							<i class="mdi mdi-lock"></i>
						</div>
					`)

					// Place blocker box over task bar minus handles
					blockerBox.style.top = `${barCoordinates.top}px`;
					blockerBox.style.left = `${barCoordinates.left + leftHandleWidth}px`;
					blockerBox.style.height = `${barCoordinates.height}px`;
					blockerBox.style.width = `${barCoordinates.width - rightHandleWidth - leftHandleWidth}px`;

					// Redirect click event when clicking the blocker
					redirectClickEvent(blockerBox, taskBarElement);

					rightHandle.addEventListener('pointerdown', () => {
						// We add this so we can disable the blocker when dragging the handle
						// Otherwise we can't drag over the blocker itself
						blockerBox.classList.add('handle-dragging')

						// Wait until mouse up to display the lock again
						const tempHandler = () => {
							blockerBox.classList.remove('handle-dragging');

							// Remove event listener
							document.removeEventListener('pointerup', tempHandler);
						}

						document.addEventListener('pointerup', tempHandler);
					})
					leftHandle.addEventListener('pointerdown', () => {
						// We add this so we can disable the blocker when dragging the handle
						// Otherwise we can't drag over the blocker itself
						blockerBox.classList.add('handle-dragging')

						// Wait until mouse up to display the lock again
						const tempHandler = () => {
							blockerBox.classList.remove('handle-dragging');

							// Remove event listener
							document.removeEventListener('pointerup', tempHandler);
						}

						document.addEventListener('pointerup', tempHandler);
					})

					// Add to container
					blockerWrapper.appendChild(blockerBox);
				}

				if (shouldBlockLeftHandle(task)) {
					leftBlockerBox = createElement(`
						<div
							class="task-interaction-blocker left-handle-blocker-box" data-task-id="${task.id}"
						>
						</div>
					`)

					// Place blocker box over task bar minus handles
					leftBlockerBox.style.top = `${barCoordinates.top}px`;
					leftBlockerBox.style.left = `${barCoordinates.left}px`;
					leftBlockerBox.style.height = `${barCoordinates.height}px`;
					leftBlockerBox.style.width = `${leftHandleWidth + 2}px`;

					if (gantt._wrapperOptions.viewOnly) {
						// Redirect handles too if on view only mode
						redirectClickEvent(leftBlockerBox, taskBarElement);
					}

					// Add to container
					blockerWrapper.appendChild(leftBlockerBox);
				}

				if (shouldBlockRightHandle(task)) {
					rightBlockerBox = createElement(`
						<div
							class="task-interaction-blocker right-handle-blocker-box" data-task-id="${task.id}"
						>
						</div>
					`)

					// Place blocker box over task bar minus handles
					rightBlockerBox.style.top = `${barCoordinates.top}px`;
					rightBlockerBox.style.left = `${barCoordinates.right - (rightHandleWidth + 1)}px`;
					rightBlockerBox.style.height = `${barCoordinates.height}px`;
					rightBlockerBox.style.width = `${rightHandleWidth + 1}px`;

					if (gantt._wrapperOptions.viewOnly) {
						// Redirect handles too if on view only mode
						redirectClickEvent(rightBlockerBox, taskBarElement);
					}

					// Add to container
					blockerWrapper.appendChild(rightBlockerBox);
				}

				const blockers = [blockerBox, leftBlockerBox, rightBlockerBox]
					// Filter only created blockers
					.filter(blocker => blocker);

				// Hovering over one blocker should trigger the other blockers too
				blockers.forEach(triggerBlocker => {
					triggerBlocker.addEventListener('pointerenter', () => {
						// One blocker triggers all blockers
						blockerWrapper.classList.add('hover');

						// We must wait a bit otherwise :hover will not be queryable
						setTimeout(() => {
							blockers.forEach(_blocker => {
								waitUntil(
									() => {
										// Wait until we are no longer hovering over any blocker...
										const hoveredElements = document.querySelectorAll(':hover');
										let stillHovering = false;
										for (const hoveredElement of hoveredElements) {
											if (blockers.includes(hoveredElement)) {
												stillHovering = true;
												break;
											}
										}

										return stillHovering === false;
									},
									{
										msBetweenAttempts: 16
									}
								).then(() => {
									// ... and remove hovering for all blockers
									blockerWrapper.classList.remove('hover');
								})
							})
						})
					})
				})

				if (blockers.length) {
					// Append blocker group to dom
					gantt._ganttContainer.appendChild(blockerWrapper);
				}
			}
		})
	}

	/**
	 * @param {GanttWrapper.Task} task
	 */
	function shouldBlockLeftHandle(task) {
		if (gantt._wrapperOptions.viewOnly) {
			return true;
		}
		return hasStartDateConstraints(task) || isParentTask(task);
	}

	/**
	 * @param {GanttWrapper.Task} task
	 */
	function shouldBlockMainBody(task) {
		if (gantt._wrapperOptions.viewOnly) {
			return true;
		}
		return hasStartDateConstraints(task) || hasEndDateConstraints(task) || isParentTask(task);
	}

	/**
	 * @param {GanttWrapper.Task} task
	 */
	function shouldBlockRightHandle(task) {
		if (gantt._wrapperOptions.viewOnly) {
			return true;
		}
		return hasEndDateConstraints(task) || isParentTask(task);
	}

	/**
	 * @param {HTMLElement} source
	 * @param {HTMLElement} target
	 */
	function redirectClickEvent(source, target) {
		source.addEventListener('click', event => {
			target.dispatchEvent(new MouseEvent('click', {
				clientX: event.clientX,
				clientY: event.clientY,
			}))
		})
	}

	/**
	 * @param {HTMLElement} source
	 * @param {HTMLElement} target
	 * @param {boolean} captureAndStopPropagation -- Useful to prevent a parent element event handler from triggering
	 */
	function redirectMouseMoveEvents(source, target, captureAndStopPropagation = false) {
		const events = [
			'focusin',
			'focusout',
			'keydown',
			'mousedown',
			'mouseenter',
			'mouseleave',
			'touchstart',
		];

		events.forEach(eventType => {
			if (captureAndStopPropagation) {
				source.addEventListener(eventType, event => {
					target.dispatchEvent(new MouseEvent(eventType, {
						clientX: event.clientX,
						clientY: event.clientY,
					}))
					event.stopPropagation();
				}, {
					capture: true,
				})
			} else {
				source.addEventListener(eventType, event => {
					target.dispatchEvent(new MouseEvent(eventType, {
						clientX: event.clientX,
						clientY: event.clientY,
					}))
				})
			}
		})

	}

	/**
	 * @param {GanttWrapper.Task} task
	 */
	function hasStartDateConstraints(task) {
		return withCache(
			task,
			'has-start-date-constraints',
			() => {
				for (const constraint of task.constraints) {
					if (constraint.type === 'starts-after' || constraint.type === 'starts-with') {
						return true;
					}
				}

				return false;
			},
		);
	}

	/**
	 * @param {GanttWrapper.Task} task
	 */
	function hasEndDateConstraints(task) {
		return withCache(
			task,
			'has-end-date-constraints',
			() => {
				for (const constraint of task.constraints) {
					if (constraint.type === 'ends-with' || constraint.type === 'ends-before') {
						return true;
					}
				}

				return false;
			},
		);
	}


	/**
	 * @param {GanttWrapper.Task} task
	 */
	function getDelayedDays(task) {
		if (taskAffectedByDelays(task) === false) {
			// Ignores any delays set
			return 0;
		}

		return task.delays.reduce(
			(delayedDays, delay) => {
				switch (delay.type) {
					case "duration":
						return delayedDays + delay.duration;
					case "range":
						throw 'Unsupported delay type';

				}

			}, 0
		)
	}

	/**
	 * @param {GanttWrapper.Task} task
	 * @return {boolean}
	 */
	function taskAffectedByDelays(task) {
		return isParentTask(task) === false && canEditEndDate(task);
	}

	/**
	 * Builds a text version of the task constraints
	 *
	 * @param {GanttWrapper.Task} task
	 * @param {boolean} [skipCache]
	 * @return {string}
	 */
	function getTaskConstraintsTextRepresentation(task, skipCache = false) {
		return withCache(
			task,
			'constraints-text-version',
			() => {
				return task.constraints.map(constraint => {
					const targetTask = gantt._findTaskById(constraint.target_task_id)

					const displayIndex = getTaskIndex(targetTask);

					let constraintType = '';

					switch (constraint.type) {
						case "starts-with":
							constraintType = 'SW'
							break;
						case "starts-after":
							// Default type can be skipped
							constraintType = ''
							break;
						case "ends-before":
							constraintType = 'EB'
							break;
						case "ends-with":
							constraintType = 'EW'
							break;
					}

					let offset = '';
					if (constraint.days_offset !== 0) {
						if (constraint.days_offset > 0) {
							offset = '+' + constraint.days_offset;
						} else {
							offset = '-' + (constraint.days_offset * -1);
						}
					}

					return displayIndex + constraintType + offset;
				}).join(", ");
			},
			skipCache,
		);
	}

	/**
	 * Builds a text description of the task constraints
	 * @param {GanttWrapper.Task} task
	 * @param {boolean} [skipCache]
	 * @return {string}
	 */
	function getTaskConstraintsTextDescription(task, skipCache = false) {
		return withCache(
			task,
			'constraints-description',
			() => {
				return task.constraints.map(constraint => {
					const targetTask = gantt._findTaskById(constraint.target_task_id)

					let offsetText = '';
					if (constraint.days_offset > 0) {
						if (constraint.days_offset === 1) {
							offsetText = '1 day after';
						} else {
							offsetText = constraint.days_offset + ' days after';
						}
					} else if (constraint.days_offset < 0) {
						if (constraint.days_offset === -1) {
							offsetText = '1 day before';
						} else {
							offsetText = (-constraint.days_offset) + ' days before';
						}
					}

					let taskDescription;

					switch (constraint.type) {
						case "starts-with":
							if (offsetText) {
								taskDescription = `Task must start no later than ${offsetText} '${targetTask.name}' starts.`;
							} else {
								taskDescription = `Task must start no later than when '${targetTask.name}' starts.`;
							}
							break;
						case "starts-after":
							if (offsetText) {
								taskDescription = `Task must start no earlier than ${offsetText} '${targetTask.name}' ends.`;
							} else {
								taskDescription = `Task must start no earlier than when '${targetTask.name}' ends.`;
							}
							break;
						case "ends-before":
							if (offsetText) {
								taskDescription = `Task must end no later than ${offsetText} '${targetTask.name}' starts.`;
							} else {
								taskDescription = `Task must end no later than when '${targetTask.name}' starts.`;
							}
							break;
						case "ends-with":
							if (offsetText) {
								taskDescription = `Task must end no earlier than ${offsetText} '${targetTask.name}' ends.`;
							} else {
								taskDescription = `Task must end no earlier than when '${targetTask.name}' ends.`;
							}
							break;
					}

					return taskDescription;
				}).join("\n");
			},
			skipCache,
		);
	}

	/**
	 * Check if the given date is a workable day
	 *
	 * @param {moment.Moment} date
	 * @return {boolean}
	 */
	function isWorkDay(date) {
		return withCache(
			null,
			'is-work-day-' + date.format('YYYYMMDD'),
			() => {
				const weekDayName = date.format('dddd').toLowerCase();

				const isWorkableWeekDay = settings.workableDays[weekDayName];

				// No need for further checking
				if (isWorkableWeekDay === false) {
					return false;
				}

				// Check if any exceptions apply
				for (const exception of settings.workableDaysExceptions) {
					if (exception.end) {
						// Exception range
						if (
							date.isSameOrAfter(exception.start, 'day') &&
							date.isSameOrBefore(exception.end, 'day')
						) {
							return false;
						}
					} else {
						// Exception day
						if (date.isSame(exception.start, 'day')) {
							return false;
						}
					}
				}

				return true;
			}
		)
	}

	/**
	 * @param {moment.Moment} date
	 * @param {'forwards'|'backwards'} direction
	 * @return {moment.Moment}
	 */
	function getClosestWorkDay(date, direction = 'forwards') {
		return withCache(
			null,
			'closest-work-day-' + direction + date.format('YYYYMMDD'),
			() => {
				date = date.clone();
				if (direction === 'forwards') {
					while (isWorkDay(date) === false) {
						date = date.add(1, 'day');
					}

					return date;
				} else {
					while (isWorkDay(date) === false) {
						date = date.subtract(1, 'day');
					}

					return date;
				}
			}
		)
	}

	/**
	 * Given a start date and a duration, find the end date that satisfies
	 * the duration while skipping non workable days
	 *
	 * @param {moment.Moment} startDate
	 * @param {number} duration
	 * @return {moment.Moment}
	 */
	function getEndDateFromDuration(startDate, duration) {
		let endDateAsMoment = startDate.clone();
		let workableDays = 1;

		while (workableDays < duration) {
			endDateAsMoment = endDateAsMoment.add(1, 'day');
			if (isWorkDay(endDateAsMoment)) {
				workableDays += 1;
			}
		}
		return endDateAsMoment;
	}

	/**
	 * Returns the work days duration between two dates
	 *
	 * @param {moment.Moment} startDate
	 * @param {moment.Moment} endDate
	 */
	function getDurationFromDates(startDate, endDate) {
		if (startDate.isSame(endDate, 'day')) {
			return 1;
		}
		if (startDate.isAfter(endDate)) {
			// Flip dates
			const temp = startDate;
			startDate = endDate;
			endDate = temp;
		}

		let workableDays = 1;

		// Clone start date
		startDate = startDate.clone();

		// Count workable days
		while (startDate.isBefore(endDate, 'day')) {
			if (isWorkDay(startDate)) {
				workableDays += 1;
			}
			startDate.add(1, 'day');
		}

		return workableDays;
	}

	/**
	 * Get child tasks that match the current browser day
	 *
	 * @returns {GanttWrapper.Task[]}
	 */
	function getCurrentTasks() {
		const today = moment(new Date());

		return getTasks().filter(task => {
			return getCurrentStartDate(task).isSameOrBefore(today) &&
				getCurrentEndDate(task).isSameOrAfter(today) &&
				isParentTask(task) === false
		})
	}

	/**
	 * Scroll to and highlight a given task
	 *
	 * @param task {Task}
	 */
	function focusTask(task) {
		// Scroll to gantt container
		if (gantt._wrapperOptions.forAdminLayout) {
			// Add offsets to compensate for admin layout
			scrollToElement(gantt._ganttContainer.parentElement, 232); // Magical
		} else {
			scrollToElement(gantt._ganttContainer.parentElement);
		}

		const animationDuration = 1000;

		const bar = getTaskRelatedBarElement(task);
		const ganttGridRow = getTaskRelatedGanttRowElement(task);
		const position = getRelativePositionFromContainer(bar);

		const scrollTop = position.top - 200; // Add some offset to get below the sticky dates container
		const scrollLeft = position.left -
			(gantt._ganttContainer.getBoundingClientRect().width / 2) // Center horizontally

		$(gantt._viewport).animate({
			scrollTop: scrollTop,
		}, animationDuration)
		$(gantt._ganttContainer).animate({
			scrollLeft: scrollLeft,
		}, animationDuration)

		bar.classList.add('focus-highlight');
		ganttGridRow.classList.add('focus-highlight');

		// Remove highlight class after a while
		setTimeout(
			() => {
				bar.classList.remove('focus-highlight');
				ganttGridRow.classList.remove('focus-highlight');
			},
			4000
		)
	}

	function displayManageWorkingDaysModal() {
		gantt._workableDaysModal.display(true);
	}

	/**
	 * Clears all workable days caches
	 */
	function clearWorkableDaysCache() {
		Object.keys(gantt._cachedValues).forEach(cacheKey => {
			if (cacheKey.includes('work-day')) {
				delete gantt._cachedValues[cacheKey];
			}
		})
	}

	/**
	 * Recalculates all tasks end dates if they, useful when the workable days
	 * change, does not refresh the view
	 */
	function recalculateEndDates() {
		gantt._getTasks().forEach(task => {
			if (task.constraints.length === 0 && isParentTask(task) === false) {
				// Only recalculate for tasks that actually require it, constrained
				// task will update their dates on the next full refresh

				setTaskStartDate(
					task,
					getCurrentStartDate(task),
					false
				)

				invalidateTaskDependants(task);
			}
		})

	}

	/**
	 * Initializes the gantt chart from a template, using the given date on the URL
	 * to calculate an offset duration for all dates
	 */
	function initializeFromTemplate() {
		const dateFromUrl = (new URLSearchParams(window.location.search)).get('date');

		const targetStartDate = moment(dateFromUrl, 'YYYY-MM-DD');

		if (getTasks().length < 1) {
			// No tasks, do nothing
			return;
		}

		// Find earliest task to compare with
		/**
		 * @type {moment.Moment|null}
		 */
		let earliestTaskStartDate = null;

		getTasks().forEach(task => {
			if (canEditStartDate(task)) {
				// Only check start date of task that that actually matter
				if (
					earliestTaskStartDate === null ||
					getCurrentStartDate(task).isBefore(earliestTaskStartDate, 'day')
				) {
					earliestTaskStartDate = getCurrentStartDate(task);
				}
			}

		})

		if (earliestTaskStartDate === null) {
			// No start date found
			console.error('No available start date found to get offset from template');
			return
		}

		// Get offset in days
		let daysOffset = targetStartDate.diff(earliestTaskStartDate, 'days');

		// Apply offset to tasks

		getTasks().forEach(task => {
			if (canEditStartDate(task)) {
				// Shift task's start date
				setTaskStartDate(
					task,
					getCurrentStartDate(task).clone().add(daysOffset, 'days'),
					false,
				)
				invalidateTaskDependants(task);
			}

			// Shift delays dates
			task.delays.forEach(delay => {
				delay.startDate.add(daysOffset, 'days')
			})
		})

		// Finally display shifted tasks
		fullRefresh();

		// Update 0-index task to min and max dates after refreshing
		let earliestDate = null
		let latestDate = null;

		getTasks().forEach(task => {
			if (
				earliestDate === null ||
				getCurrentStartDate(task).isBefore(earliestDate, 'day')
			) {
				earliestDate = getCurrentStartDate(task);
			}
			if (
				latestDate === null ||
				getCurrentEndDate(task).isAfter(latestDate, 'day')
			) {
				latestDate = getCurrentEndDate(task);
			}
		})

		// Update index-0 task
		setTaskStartDate(gantt._all_tasks[0], earliestDate, false)
		setTaskEndDate(gantt._all_tasks[0], latestDate, false)

		// Add a flag to the container
		gantt._ganttContainer.classList.add('from-template-initialized');
	}

	/**
	 * Updates the toolbar status like tooltips based on task selection status
	 */
	function updateToolbarForTaskSelections() {
		// Update collapse buttons
		const collapseButton = gantt._toolBarContainer
			.querySelector('.collapse-button');
		const expandButton = gantt._toolBarContainer
			.querySelector('.expand-button');

		const selectedTasks = getSelectedTasks();

		if (selectedTasks.length) {
			collapseButton.title = 'Collapse Tasks';
			expandButton.title = 'Expand Tasks';
		} else {
			collapseButton.title = 'Collapse All';
			expandButton.title = 'Expand All';
		}

		// Update printable flag buttons
		const enablePrintableFlag = gantt._toolBarContainer
			.querySelector('.enable-printable-flag-button');
		const disablePrintableFlag = gantt._toolBarContainer
			.querySelector('.disable-printable-flag-button');

		if (selectedTasks.length) {
			enablePrintableFlag.title = 'Enable Printing';
			disablePrintableFlag.title = 'Disable Printing';
		} else {
			enablePrintableFlag.title = 'Enable Printing For All';
			disablePrintableFlag.title = 'Disable Printing For All';
		}
	}

	/**
	 * Stores selected tasks to be reselected after a refresh
	 */
	function storeSelectedTasks() {
		gantt._gridSelectedTasks = getSelectedTasks();
	}

	/**
	 * Restores selected tasks to the grid, filtering non existing tasks
	 */
	function restoreSelectedTasks() {
		gantt._gridSelectedTasks = gantt._gridSelectedTasks
			.map(task => {
				// Query task by id again to get a fresh copy
				return findTaskById(task.id);
			})
			.filter(task => task); // Remove non existing tasks

		// Map to id map
		const taskIdMap = gantt._gridSelectedTasks.reduce((map, task) => {
			map[task.id] = task;

			return map;
		}, {});

		// Reselect rows/tasks
		getGridApi().forEachNode(node => {
			// Get selected rows should be selected
			if (taskIdMap[node.data.id] !== undefined) {
				node.setSelected(true, false);
			}
		})
	}

	function restoreLastFocusedCell() {
		const gridApi = getGridApi();
		// Preserve cell focus after reload
		if (gantt._lastFocusedGridCell) {
			// Save the cell in case it gets cleared up before the timeout its over
			const lastFocusedGridCell = gantt._lastFocusedGridCell;

			// We need to set a timeout, because even though the grid is
			// using a local data source it still uses a promise to retrieve rows and
			// so it asynchronously refreshes, so we wait a bit before refocusing the cell
			setTimeout(() => {
				// Refocus
				gridApi.setFocusedCell(lastFocusedGridCell.rowIndex, lastFocusedGridCell.column)
				// Clear last focused cell
				gantt._lastFocusedGridCell = undefined;
			}, 16)
		}
	}

	/**
	 * If a refresh is in progress, delays execution until it has finished
	 * @param {function} callback
	 */
	function waitUntilRefreshFinished(callback) {
		if (gantt._refreshing) {
			setTimeout(() => waitUntilRefreshFinished(callback));
			return;
		}

		return callback();
	}

	/**
	 * Ag grid handlers, global for all drag options
	 */
	function onDragStart() {
		// Init scroll if dragging a task process
		waitUntil(() => {
			// Wait until a move event has been triggered
			return gantt._lastTaskDragEvent !== undefined
		}).then(scrollIfDragging)
	}

	/**
	 * Ag grid handlers, global for all drag options
	 */
	function onDragStopped() {
		// Clear last drag event
		gantt._lastTaskDragEvent = undefined;
		clearDropHighlightedRows();
	}

	function scrollIfDragging() {
		if (gantt._lastTaskDragEvent === undefined) {
			// Stop checking if scrolling is needed
			return;
		}

		// We use the AG grid drag & drop indicator to know where the cursor is
		const dragAndDropIndicator = document.querySelector('.ag-dnd-ghost');

		if (dragAndDropIndicator) {
			const dragAndDropBox = dragAndDropIndicator.getBoundingClientRect();
			const viewPortBox = gantt._viewport.getBoundingClientRect();

			const scrollStartDistance = 60;
			const scrollTopPadding = 40;
			if (dragAndDropBox.bottom > (viewPortBox.bottom - scrollStartDistance)) {
				// Scroll down
				scrollViewport(8);
			} else if (dragAndDropBox.top < (viewPortBox.top + scrollStartDistance + scrollTopPadding)) {
				// Scroll up
				scrollViewport(-8);
			}
		}

		// Keep checking if scrolling is needed
		setTimeout(scrollIfDragging, 8);
	}

	/**
	 * @param {import('ag-grid-community/dist/lib/events').RowDragEvent} e
	 */
	function onTaskRowDragEnd(e) {
		clearDropHighlightedRows();

		// Get the hovered task
		if (e.overNode && e.overNode.data && e.overNode.data.id) {
			const targetTask = findTaskById(e.overNode.data.id);

			if (targetTask) {
				// Get the dragged task
				const sourceTask = findTaskById(e.node.data.id);

				if (targetTask.id === sourceTask.id) {
					// Ignore dropping on the same task
					return;
				}

				// Check if target task has a parent
				const targetTaskParents = getTaskParents(targetTask);

				if (targetTaskParents.length > 0) {
					// Check if target task is child of source task
					let isParent = false;

					for (const parent of targetTaskParents) {
						if (parent.id === sourceTask.id) {
							isParent = true;
							break;
						}
					}

					if (isParent) {
						// Cancel drop
						return;
					}

					// Make sibling of target task
					setTaskParent(sourceTask, targetTaskParents[0]);
				} else {
					// Make root task
					sourceTask.dependencies.length = 0;
					invalidateTaskDependants(sourceTask, true);
				}

				// Put source task above target task
				let targetIndex = getTaskIndex(targetTask);
				const sourceIndex = getTaskIndex(sourceTask);
				// If target is below source AND we are moving more than one position...
				if (targetIndex > sourceIndex && (targetIndex - sourceIndex > 1)) {
					// ... subtract one because we want to place it above the target
					targetIndex -= 1;
				}

				// Remove from old position
				gantt._all_tasks.splice(sourceIndex, 1)

				// Put in new position
				gantt._all_tasks.splice(targetIndex, 0, sourceTask);

				// Clear task selection
				gantt._gridSelectedTasks.length = 0;

				fullRefresh();
			}
		}
	}

	/**
	 * @param {import('ag-grid-community/dist/lib/events').RowDragEvent} e
	 */
	function onTaskRowDragMove(e) {
		// Clear highlighted rows
		clearDropHighlightedRows();

		// Highlight current row if any
		if (e.overIndex >= 0) {
			const rowElement = gantt._taskPanelContainer
				.querySelector(`.ag-row[row-index="${e.overIndex}"]`);

			rowElement.classList.add('drop-highlight');

			// Check if target task is a child of source task
			const targetTask = findTaskById(e.overNode.data.id);
			const sourceTask = findTaskById(e.node.data.id);

			const targetParents = getTaskParents(targetTask);

			let isParent = false;

			for (const parent of targetParents) {
				if (parent.id === sourceTask.id) {
					isParent = true;
					break;
				}
			}

			if (isParent) {
				rowElement.classList.add('invalid-drop');
			}
		}

		gantt._lastTaskDragEvent = e;
	}

	function clearDropHighlightedRows() {
		gantt._taskPanelContainer.querySelectorAll('.ag-row').forEach(rowElement => {
			rowElement.classList.remove('drop-highlight');
			rowElement.classList.remove('invalid-drop');
		})
	}

	// noinspection JSUnusedLocalSymbols -- May be used in the future
	/**
	 * @param {HTMLElement} rowElement
	 */
	function startScrollToRow(rowElement) {
		// Start scrolling process
		if (gantt._scrollingToRow === undefined) {
			setTimeout(scrollToRow, 0);
		}
		// Store target row
		gantt._scrollingToRow = rowElement;
	}


	function scrollToRow() {
		if (gantt._scrollingToRow === undefined) {
			// Scrolling canceled
			return;
		}

		const rowBoundingBox = gantt._scrollingToRow.getBoundingClientRect();
		const viewPortBoundingBox = gantt._viewport.getBoundingClientRect();

		let scrolling = false;

		const scrollStartDistance = 60;
		const scrollTopPadding = 40;
		if (rowBoundingBox.bottom > (viewPortBoundingBox.bottom - scrollStartDistance)) {
			// Scroll down
			scrollViewport(8);
			scrolling = true;
		} else if (rowBoundingBox.top < (viewPortBoundingBox.top + scrollStartDistance + scrollTopPadding)) {
			// Scroll down
			scrollViewport(-8);
			scrolling = true;
		} else {
			// Row is fully visible, stop scrolling
			gantt._scrollingToRow = undefined;
		}

		if (scrolling) {
			// Keep scrolling until row is fully visible
			setTimeout(scrollToRow, 8);
		}
	}

	/**
	 *
	 * @param distanceInPixels
	 */
	function scrollViewport(distanceInPixels) {
		gantt._viewport.scrollTop += distanceInPixels;
	}

	/**
	 * Returns the months going from the earliest task to the latest task
	 * along with the tasks that 'belong' to each month
	 *
	 * @returns {GanttWrapper.TimelineMonth[]}
	 */
	function getTimelineMonths() {
		/** @var {GanttWrapper.TimelineMonth[]} */
		const months = [];

		const tasks = getTasks();

		if (tasks.length === 0) {
			return months;
		}

		// Get earliest start date and latest end dates
		// so we can start adding months
		let earliestTaskStart = getCurrentStartDate(tasks[0]);
		let latestTaskStart = getCurrentEndDate(tasks[0]);

		tasks.forEach(task => {
			const startDate = getCurrentStartDate(task);
			const endDate = getCurrentEndDate(task);

			if (startDate.isBefore(earliestTaskStart)) {
				earliestTaskStart = startDate;
			}
			if (endDate.isAfter(latestTaskStart)) {
				latestTaskStart = endDate;
			}
		})

		// Get start of earliest month and finish of latest month
		const earliestMonthStartDate = earliestTaskStart.clone().startOf('month');
		const latestMonthEndDate = latestTaskStart.clone().endOf('month');

		let currenMonthStartDate = earliestMonthStartDate;

		do {
			const currentMonthEndDate = currenMonthStartDate.clone().endOf('month');

			const monthTasks = tasks.filter(task => {
				return getCurrentStartDate(task).isSameOrAfter(currenMonthStartDate) &&
					getCurrentEndDate(task).isSameOrBefore(currentMonthEndDate)
			})

			months.push({
				name: currenMonthStartDate.format('MMMM').toUpperCase(),
				year: currenMonthStartDate.format('YYYY'),
				tasks: monthTasks,
				endDate: currentMonthEndDate,
				startDate: currenMonthStartDate.clone(),
				type: 'month',
			})

			currenMonthStartDate.add('1', 'month');
		} while (currenMonthStartDate.isBefore(latestMonthEndDate))

		return months;
	}

	/**
	 * Returns the quarters going from the earliest task to the latest task
	 * along with the tasks that 'belong' to each quarter
	 *
	 * @returns {GanttWrapper.TimelineQuarter[]}
	 */
	function getTimelineQuarters() {
		/** @var {GanttWrapper.TimelineQuarter[]} */
		const quarters = [];

		const tasks = getTasks();

		if (tasks.length === 0) {
			return quarters;
		}

		// Get earliest start date and latest end dates
		// so we can start adding quarters
		let earliestTaskStart = getCurrentStartDate(tasks[0]);
		let latestTaskStart = getCurrentEndDate(tasks[0]);

		tasks.forEach(task => {
			const startDate = getCurrentStartDate(task);
			const endDate = getCurrentEndDate(task);

			if (startDate.isBefore(earliestTaskStart)) {
				earliestTaskStart = startDate;
			}
			if (endDate.isAfter(latestTaskStart)) {
				latestTaskStart = endDate;
			}
		})

		// Get start of earliest quarter and finish of latest quarter
		const earliestQuarterStartDate = earliestTaskStart.clone().startOf('quarter');
		const latestQuarterEndDate = latestTaskStart.clone().endOf('quarter');

		let currenQuarterStartDate = earliestQuarterStartDate;

		do {
			const currentQuarterEndDate = currenQuarterStartDate.clone().endOf('quarter');

			const quarterTasks = tasks.filter(task => {
				return getCurrentStartDate(task).isSameOrAfter(currenQuarterStartDate) &&
					getCurrentEndDate(task).isSameOrBefore(currentQuarterEndDate)
			})

			const quarterNumber = currenQuarterStartDate.quarter();

			let quarterName;

			switch (quarterNumber) {
				case 1:
					quarterName = '1st QUARTER';
					break;
				case 2:
					quarterName = '2nd QUARTER';
					break;
				case 3:
					quarterName = '3rd QUARTER';
					break;
				case 4:
					quarterName = '4th QUARTER';
					break;
				default:
					quarterName = 'Unknown quarter';
					break;
			}

			quarters.push({
				name: quarterName,
				year: currenQuarterStartDate.format('YYYY'),
				tasks: quarterTasks,
				endDate: currentQuarterEndDate,
				startDate: currenQuarterStartDate.clone(),
				type: 'quarter',
			})

			currenQuarterStartDate.add('1', 'quarter');
		} while (currenQuarterStartDate.isBefore(latestQuarterEndDate))

		return quarters;
	}

	/**
	 * @param {GanttWrapper.ExportTasksOptions} options
	 * @return {GanttWrapper.ExportedTask[]}
	 */
	function exportTasks(options) {
		// Export tasks as a tree
		if (options.allTasks) {
			return getRootTasks().map(task => getTaskExport(task, options));
		} else {
			// Get only printable tasks
			return getRootTasks()
				.filter(task => task.printable)
				.map(task => getTaskExport(task, options));
		}
	}

	/**
	 * @param {GanttWrapper.Task} task
	 * @param {GanttWrapper.ExportTasksOptions} options
	 * @return {GanttWrapper.ExportedTask}
	 */
	function getTaskExport(task, options) {
		const duration = getTaskActualDuration(task);
		const delayedDays = getDelayedDays(task);

		/** @var {GanttWrapper.ExportedTask} */
		const taskExport = {
			id: task.id,
			name: task.name,
			startDateString: getCurrentStartDate(task).format('YYYY-MM-DD'),
			endDateString: getCurrentEndDate(task).format('YYYY-MM-DD'),
			progress: task.progress,
			subTasks: [],
			baseDuration: duration - delayedDays,
			delayDuration: delayedDays,
			totalDuration: duration,
		}

		// Get task children as exports
		if (options.allTasks) {
			taskExport.subTasks = getChildTasks(task).map(task => getTaskExport(task, options));
		} else {
			// Get only printable children
			taskExport.subTasks = getChildTasks(task)
				.filter(task => task.printable)
				.map(task => getTaskExport(task, options));
		}


		return taskExport;
	}

	/**
	 * @param {GanttWrapper.ExportTasksOptions} taskOptions
	 * @return {GanttWrapper.TimelineExport}
	 */
	function exportTimeline(taskOptions) {
		return {
			name: gantt.__timeline_name,
			related_budget_name: gantt.__related_budget_name,
			tasks: gantt._exportTasks(taskOptions),
		}
	}

	/**
	 * Exports the user UI settings for later retrieval
	 * @return {GanttWrapper.UISettings}
	 */
	function exportUISettings() {
		try {
			return {
				ganttViewType: gantt._viewTypeSelect.value,
				editorViewType: gantt._editorViewType,
				collapsedTasks: gantt._getTasks()
					.filter(task => task.collapsed).map(task => {
						return {
							id: task.id,
						}
					}),
				editorSplitXPosition: gantt._taskPanelWidth,
				ganttScrollLeft: gantt._ganttContainer.scrollLeft,
				ganttScrollTop: gantt._viewport.scrollTop,
				columnState: getColumnApi().getColumnState(),
			}
		} catch (e) {
			// If something goes wrong export, just return an empty object
			console.error('Error exporting UI settings');
			console.error(e);
			// noinspection JSValidateTypes
			return {}
		}
	}

	function loadStoredUISettings() {
		const uiSettings = gantt._wrapperOptions.UISettings;

		//// Restore collapsed tasks
		if (uiSettings.collapsedTasks) {
			uiSettings.collapsedTasks.forEach(collapsedTask => {
				toggleTaskCollapsedStatus(
					findTaskById(collapsedTask.id),
					true,
					false,
				)
			})
		}

		// Restore editor view type
		if (uiSettings.editorViewType) {
			gantt._editorViewTypeSelect.value = uiSettings.editorViewType;
			gantt._editorViewType = uiSettings.editorViewType;

			handleEditorViewTypeChange();
		}

		// Restore gantt view type
		if (uiSettings.ganttViewType) {
			gantt._viewTypeSelect.value = uiSettings.ganttViewType;

			gantt.change_view_mode(uiSettings.ganttViewType)
		}

		// Restore task panel width position
		if (uiSettings.editorSplitXPosition) {
			gantt._taskPanelWidth = uiSettings.editorSplitXPosition

			if (gantt._viewTypeSelect.value === 'split') {
				enforceTaskPanelWidthLimits();
			}
		}

		// Restore scroll positions
		if (uiSettings.ganttScrollTop) {
			gantt._viewport.scrollTop = uiSettings.ganttScrollTop;
		}

		if (uiSettings.ganttScrollLeft) {
			gantt._ganttContainer.scrollLeft = uiSettings.ganttScrollLeft;
		}

		if (uiSettings.columnState) {
			// We need to put a timeout because something is overriding
			// the resize column widths
			setTimeout(() => {
				try {
					getColumnApi().applyColumnState({
						state: uiSettings.columnState,
						applyOrder: true,
					})
				} catch (e) {
					console.error('Error restoring task panel column state');
					console.error(e);
				}
			}, 2200) // TODO: find a better solution so this timeout is not needed
		}
	}
}

/**
 * Format that frappe gantt uses, also used to serialize dates for the server.
 * Read back as an HTML date there.
 *
 * @param {Moment} date instance
 */
function formatDateForGanttChart(date) {
	// noinspection JSUnresolvedFunction
	return date.format('YYYY-MM-DD');
}

// noinspection JSUnusedGlobalSymbols -- Imported via d.ts file
/**
 * @param {import('./ganttWrapper').GanttWrapper.ConstraintType} type
 * @return {string}
 */
function getConstraintTypeDisplayName(type) {
	switch (type) {
		case "ends-before":
			return 'End Before';
		case "ends-with":
			return 'End With';
		case "starts-after":
			return 'Start After';
		case "starts-with":
			return 'Start With';
		default:
			return type;
	}
}

export {createGantt, formatDateForGanttChart, getConstraintTypeDisplayName}
